import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import {
  Chart as ChartJS,
  RadialLinearScale,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { PolarArea, Bar } from 'react-chartjs-2';
import { BOOKMAKERS_LIST, BOOKMAKER_ALIASES, isExcludedFromBestBook } from '../utils/constants';
import { buildBullpenList } from '../utils/mlbBullpen';
import { getTeamLogoUrl } from '../utils/formatters';
import { useDarkMode } from '../contexts/DarkModeContext';
import PlayerGameLogChart from './PlayerGameLogChart';
import PlayerStatsChart from './PlayerStatsChart';
import { getPrimaryLinePoint } from '../utils/gameLogChartConfig';
import BullpenDropdown from './BullpenDropdown';
import { useBooks } from '../contexts/BooksContext';

ChartJS.register(RadialLinearScale, ArcElement, CategoryScale, LinearScale, BarElement, ChartTooltip, ChartLegend);

// Market categories and display names (matches NBAPlayerModal)
const NBA_MARKET_CATEGORIES = [
  { category: 'points', marketKey: 'player_points', name: 'PTS' },
  { category: 'rebounds', marketKey: 'player_rebounds', name: 'REB' },
  { category: 'assists', marketKey: 'player_assists', name: 'AST' },
  { category: 'threes', marketKey: 'player_threes', name: '3PM' },
  { category: 'pra', marketKey: 'player_points_rebounds_assists', name: 'PRA' },
  { category: 'points_rebounds', marketKey: 'player_points_rebounds', name: 'P+R' },
  { category: 'points_assists', marketKey: 'player_points_assists', name: 'P+A' },
  { category: 'rebounds_assists', marketKey: 'player_rebounds_assists', name: 'R+A' },
  { category: 'blocks', marketKey: 'player_blocks', name: 'BLK' },
  { category: 'steals', marketKey: 'player_steals', name: 'STL' },
  { category: 'double_double', marketKey: 'player_double_double', name: 'DD' },
  { category: 'triple_double', marketKey: 'player_triple_double', name: 'TD' },
  { category: 'first_basket', marketKey: 'player_first_basket', name: '1st' },
  { category: 'first_team_basket', marketKey: 'player_first_team_basket', name: '1st Tm' },
];

const MLB_MARKET_CATEGORIES = [
  { category: 'batter_hits', marketKey: 'batter_hits', name: 'H' },
  { category: 'batter_singles', marketKey: 'batter_singles', name: '1B' },
  { category: 'batter_doubles', marketKey: 'batter_doubles', name: '2B' },
  { category: 'batter_triples', marketKey: 'batter_triples', name: '3B' },
  { category: 'batter_home_runs', marketKey: 'batter_home_runs', name: 'HR' },
  { category: 'batter_total_bases', marketKey: 'batter_total_bases', name: 'TB' },
  { category: 'batter_rbis', marketKey: 'batter_rbis', name: 'RBI' },
  { category: 'batter_runs_scored', marketKey: 'batter_runs_scored', name: 'R' },
  { category: 'batter_hits_runs_rbis', marketKey: 'batter_hits_runs_rbis', name: 'H+R+R' },
  { category: 'batter_stolen_bases', marketKey: 'batter_stolen_bases', name: 'SB' },
  { category: 'pitcher_strikeouts', marketKey: 'pitcher_strikeouts', name: 'K' },
  { category: 'pitcher_hits_allowed', marketKey: 'pitcher_hits_allowed', name: 'Hits' },
  { category: 'pitcher_outs', marketKey: 'pitcher_outs', name: 'Outs' },
];

const NFL_MARKET_CATEGORIES = [
  { category: 'player_pass_yds', marketKey: 'player_pass_yds', name: 'PaYds' },
  { category: 'player_rush_yds', marketKey: 'player_rush_yds', name: 'RuYds' },
  { category: 'player_reception_yds', marketKey: 'player_reception_yds', name: 'ReYds' },
  { category: 'player_receptions', marketKey: 'player_receptions', name: 'REC' },
  { category: 'player_rush_reception_yds', marketKey: 'player_rush_reception_yds', name: 'Ru+RecYds' },
  { category: 'player_field_goals', marketKey: 'player_field_goals', name: 'FG' },
  { category: 'player_tds_over', marketKey: 'player_tds_over', name: 'TDs' },
  { category: 'player_1st_td', marketKey: 'player_1st_td', name: '1st TD' },
  { category: 'player_pass_tds', marketKey: 'player_pass_tds', name: 'PaTDs' },
  { category: 'player_rush_attempts', marketKey: 'player_rush_attempts', name: 'RuAtt' },
];

const SOCCER_MARKET_CATEGORIES = [
  { category: 'player_goal_scorer_anytime', marketKey: 'player_goal_scorer_anytime', name: 'Goal' },
  { category: 'player_shots', marketKey: 'player_shots', name: 'SHO' },
  { category: 'player_shots_on_target', marketKey: 'player_shots_on_target', name: 'SOT' },
  { category: 'player_first_goal_scorer', marketKey: 'player_first_goal_scorer', name: '1st Goal' },
  { category: 'player_last_goal_scorer', marketKey: 'player_last_goal_scorer', name: 'Last Goal' },
];

const NHL_MARKET_CATEGORIES = [
  { category: 'goals', marketKey: 'player_goals', name: 'Goals' },
  { category: 'assists', marketKey: 'player_assists', name: 'Assists' },
  { category: 'points', marketKey: 'player_points', name: 'Points' },
  { category: 'shots_on_goal', marketKey: 'player_shots_on_goal', name: 'SOG' },
  { category: 'goal_scorers', marketKey: 'player_goal_scorer_first', name: '1st/Last', combine: 'player_goal_scorer_last' },
  { category: 'blocked', marketKey: 'player_blocked_shots', name: 'BLK' },
  { category: 'saves', marketKey: 'player_saves', name: 'Saves' },
];

const getMarketCategories = (sport) => {
  switch (sport) {
    case 'nba': case 'wnba': return NBA_MARKET_CATEGORIES;
    case 'mlb': return MLB_MARKET_CATEGORIES;
    case 'nfl': case 'ncaaf': return NFL_MARKET_CATEGORIES;
    case 'soccer': return SOCCER_MARKET_CATEGORIES;
    case 'nhl': return NHL_MARKET_CATEGORIES;
    default: return NBA_MARKET_CATEGORIES;
  }
};

// Same logic as NBAPlayerModal.processMarketLines
const processMarketLines = (marketLines, marketKey) => {
  return marketLines.map(line => {
    const books = {};
    const isBinaryMarket = line.point === 'yes' || line.point === 'no' || line.isBinary === true;

    Object.keys(line).forEach(key => {
      if (key.startsWith(`${marketKey}_`) &&
          !key.includes('bestBook') &&
          !key.includes('nextBook') &&
          !key.includes('Diff') &&
          !key.includes('Price') &&
          !key.includes('Count')) {
        const suffix = key.replace(`${marketKey}_`, '');
        const parts = suffix.split('_');

        if (parts.length >= 2) {
          const bookName = parts.slice(0, -1).join('_');
          const field = parts[parts.length - 1];

          if (field === 'over' || field === 'under' || field === 'yes' || field === 'no' || field === 'point' || field === 'isAlternate' || field === 'isAvailable' || field === 'isMain') {
            if (!books[bookName]) books[bookName] = {};
            books[bookName][field] = line[key];
          }
          if (field === 'over' || field === 'under' || field === 'yes') {
            const availKey = `${marketKey}_${bookName}_${field}_isAvailable`;
            if (line[availKey] !== undefined) {
              books[bookName][`${field}_isAvailable`] = line[availKey];
            }
            const mainKey = `${marketKey}_${bookName}_${field}_isMain`;
            if (line[mainKey] !== undefined) {
              books[bookName][`${field}_isMain`] = line[mainKey];
            }
          }
          if (!books[bookName]?.isAvailable) {
            const availKey = `${marketKey}_${bookName}_isAvailable`;
            if (line[availKey] !== undefined) {
              if (!books[bookName]) books[bookName] = {};
              books[bookName].isAvailable = line[availKey];
              if (books[bookName].yes !== undefined) {
                books[bookName].yes_isAvailable = line[availKey];
              }
            }
          }
        } else if (parts.length === 1 && isBinaryMarket) {
          const bookName = suffix;
          if (!books[bookName]) books[bookName] = {};
          books[bookName].yes = line[key];
        }
      }
    });

    return {
      point: line.point === 'yes' ? null : line.point,
      label: line.point === 'yes' ? 'Yes' : `${line.point}`,
      books: books,
      bestBook: line[`${marketKey}_bestBook`],
      bestBookPrice: line[`${marketKey}_bestBookPrice`],
      bestBookDiff: line[`${marketKey}_bestBookDiff`],
      isSpecial: line.point === 'yes' || line.point === 'no' || line.isBinary === true
    };
  }).sort((a, b) => {
    if (a.isSpecial && !b.isSpecial) return 1;
    if (!a.isSpecial && b.isSpecial) return -1;
    return (a.point || 0) - (b.point || 0);
  });
};

// Process MLB format (has availableMarkets array)
const processMLBData = (data) => {
  const markets = [];
  const availableMarkets = data.availableMarkets || [];

  availableMarkets.forEach(marketKey => {
    const marketData = data[marketKey];
    if (!marketData || typeof marketData !== 'object') return;

    const allLines = [];
    ['over_under', 'alternates'].forEach(oddsType => {
      if (marketData[oddsType] && Array.isArray(marketData[oddsType])) {
        marketData[oddsType].forEach(tierData => {
          if (tierData.books && tierData.point !== undefined) {
            allLines.push({
              point: tierData.point,
              books: tierData.books,
              bestBook: tierData.bestBook,
              bestBookDiff: tierData.bestBookDiff,
              isSpecial: false,
              label: `${tierData.point}`,
              isOverUnder: oddsType === 'over_under'
            });
          }
        });
      }
    });

    if (allLines.length > 0) {
      // Deduplicate by point, preferring over_under
      const byPoint = {};
      allLines.forEach(l => {
        if (!byPoint[l.point] || l.isOverUnder) {
          byPoint[l.point] = l;
        }
      });
      const lines = Object.values(byPoint).sort((a, b) => (a.point || 0) - (b.point || 0));

      const catDef = MLB_MARKET_CATEGORIES.find(c => c.marketKey === marketKey);
      markets.push({
        marketKey,
        marketName: catDef ? catDef.name : marketKey,
        category: marketKey,
        lines
      });
    }
  });

  return markets;
};

// Process generic flat format (NBA, NFL, etc.)
const processGenericData = (data, sport) => {
  const categories = getMarketCategories(sport);
  const markets = [];

  // Collect keys that are handled via combine so we skip them as standalone
  const combinedKeys = new Set();
  categories.forEach(c => {
    if (c.combine) combinedKeys.add(c.combine);
  });

  // Also detect market keys in the response data
  const dataKeys = Object.keys(data).filter(key =>
    Array.isArray(data[key]) && data[key].length > 0 && data[key][0].point !== undefined
  );
  const allMarketKeys = [...new Set([
    ...categories.map(c => c.marketKey),
    ...dataKeys
  ])];

  console.log('[processGenericData] sport:', sport, 'allMarketKeys:', allMarketKeys, 'dataKeys:', dataKeys);
  allMarketKeys.forEach(marketKey => {
    // Skip keys that are combined into another market
    if (combinedKeys.has(marketKey)) return;
    // Skip _null variants when the base market key exists in the data
    if (/_null$/i.test(marketKey) && data[marketKey.replace(/_null$/i, '')]) return;
    // Skip goal_scorer_anytime for NHL (covered by goals market)
    if (sport === 'nhl' && marketKey === 'player_goal_scorer_anytime') return;

    if (!data[marketKey] || !Array.isArray(data[marketKey])) {
      console.log('[processGenericData] SKIP', marketKey, '- missing or not array:', !!data[marketKey], Array.isArray(data[marketKey]));
      return;
    }

    const catDef = categories.find(c => c.marketKey === marketKey);

    // Handle combined markets (e.g. first + last goal scorer)
    if (catDef && catDef.combine) {
      const firstLines = processMarketLines(data[marketKey], marketKey).map(l => ({
        ...l,
        label: '1st',
        originalMarketKey: marketKey
      }));
      const secondLines = data[catDef.combine] && Array.isArray(data[catDef.combine])
        ? processMarketLines(data[catDef.combine], catDef.combine).map(l => ({
            ...l,
            label: 'Last',
            originalMarketKey: catDef.combine
          }))
        : [];
      const combinedLines = [...firstLines, ...secondLines];
      if (combinedLines.length === 0) return;
      markets.push({
        marketKey,
        marketName: catDef.name,
        category: catDef.category,
        lines: combinedLines
      });
      return;
    }

    const lines = processMarketLines(data[marketKey], marketKey);
    if (lines.length === 0) return;

    markets.push({
      marketKey,
      marketName: catDef ? catDef.name : marketKey.replace('player_', '').replace(/_null$/i, '').replace(/_/g, ' ').toUpperCase(),
      category: catDef ? catDef.category : marketKey,
      lines
    });
  });

  return markets;
};

// Edge between two american-odds prices (same formula as recalculateEdge)
const calculateEdge = (price, ref) => {
  if (price == null || ref == null) return 0;
  if (price > 0 && ref < 0) {
    const totalSpread = (price - 100) - (ref - (-100));
    return Math.abs(totalSpread / ref);
  }
  return (price - ref) / Math.abs(ref);
};

// Per-side: identify the primary best book (excluding Secondary books) and
// collect each Secondary book that ties or beats primary, with its edge.
const computeSideBest = (books, side) => {
  if (!books) return null;
  // Skip books where this side's odds are flagged unavailable — otherwise
  // a stale/unavailable price (e.g. bet365 with isAvailable:false) can tie
  // the top price and suppress the highlight on the actual best book.
  const entries = Object.entries(books)
    .filter(([, data]) => {
      if (!data) return false;
      if (data.isAvailable === false) return false;
      if (data[`${side}_isAvailable`] === false) return false;
      return true;
    })
    .map(([book, data]) => [book, data?.[side]])
    .filter(([, price]) => price !== null && price !== undefined);
  if (entries.length === 0) return null;

  const nonExcluded = entries.filter(([book]) => !isExcludedFromBestBook(book));
  if (nonExcluded.length === 0) return null;

  const sorted = [...nonExcluded].sort((a, b) => b[1] - a[1]);
  const primaryPrice = sorted[0][1];
  const tiedAtTop = sorted.filter(([, p]) => p === primaryPrice);
  const primaryBook = tiedAtTop.length === 1 ? sorted[0][0] : null; // tied → no unique primary highlight
  const secondPrice = sorted.find(([, p]) => p !== primaryPrice)?.[1];
  const primaryEdge = (primaryBook && secondPrice != null) ? calculateEdge(primaryPrice, secondPrice) : 0;

  const secondaryEdges = {};
  entries.filter(([book]) => isExcludedFromBestBook(book)).forEach(([book, price]) => {
    if (price >= primaryPrice) {
      secondaryEdges[book] = price === primaryPrice ? 0 : calculateEdge(price, primaryPrice);
    }
  });

  return { primaryBook, primaryPrice, primaryEdge, secondaryEdges };
};

// Tier styling: primary best gets the full background; Secondary books that
// tie/beat primary get a corner caret tier-colored by their edge.
const getTierStyle = (line, book, side) => {
  const odds = line.books?.[book]?.[side];
  if (odds === null || odds === undefined) return {};

  const sideBest = computeSideBest(line.books, side);
  if (!sideBest) return {};

  const isPrimary = book === sideBest.primaryBook;
  const secondaryEdge = sideBest.secondaryEdges[book];
  const isSecondaryWithEdge = secondaryEdge !== undefined;

  let style = { fontWeight: (isPrimary || isSecondaryWithEdge) ? 'bold' : 'normal' };

  if (isPrimary) {
    const diff = sideBest.primaryEdge;
    if (diff >= 0.25) {
      Object.assign(style, { backgroundColor: 'green', color: 'yellow', borderRadius: '2px' });
    } else if (diff >= 0.15) {
      Object.assign(style, { backgroundColor: '#FFC107', color: '#212529', borderRadius: '2px' });
    } else if (diff >= 0.05) {
      Object.assign(style, { backgroundColor: '#3B82F6', color: 'white', borderRadius: '2px' });
    } else {
      Object.assign(style, { color: '#F59E0B' });
    }
    return style;
  }

  if (isSecondaryWithEdge) {
    const caretColor = secondaryEdge >= 0.25 ? '#22c55e'
      : secondaryEdge >= 0.15 ? '#F59E0B'
      : secondaryEdge >= 0.05 ? '#3B82F6'
      : '#9CA3AF';
    Object.assign(style, {
      backgroundImage: `linear-gradient(225deg, ${caretColor} 7px, transparent 7px)`
    });
  }

  return style;
};

// Pitch type code → short display name
const PITCH_TYPE_MAP = {
  FF: '4-Seam', SI: 'Sinker', CU: 'Curve', SL: 'Slider', CH: 'Change',
  FC: 'Cutter', FS: 'Split', KC: 'Knuckle-C', ST: 'Sweep', SV: 'Slurve', KN: 'Knuckle'
};

// Zone center coordinates (feet) for fallback when pX/pZ not available
const ZONE_CENTERS = (() => {
  const L = -0.708, R = 0.708, B = 1.5, T = 3.5;
  const tw = (R - L) / 3, th = (T - B) / 3;
  return {
    1: [L + tw * 0.5, T - th * 0.5], 2: [0, T - th * 0.5], 3: [R - tw * 0.5, T - th * 0.5],
    4: [L + tw * 0.5, (T + B) / 2],  5: [0, (T + B) / 2],  6: [R - tw * 0.5, (T + B) / 2],
    7: [L + tw * 0.5, B + th * 0.5], 8: [0, B + th * 0.5], 9: [R - tw * 0.5, B + th * 0.5],
    11: [L - 0.4, T + 0.4], 12: [R + 0.4, T + 0.4],
    13: [L - 0.4, B - 0.4], 14: [R + 0.4, B - 0.4],
  };
})();

const PitchZoneGrid = ({ pitchEvents, large, mobile: mobileOverride }) => {
  const isMobileView = mobileOverride ?? (typeof window !== 'undefined' && window.innerWidth < 768);
  const pitchList = [];

  (pitchEvents || []).forEach(pe => {
    if (!pe.pitchNumber) return;

    const pX = pe.pitchData?.coordinates?.pX;
    const pZ = pe.pitchData?.coordinates?.pZ;
    const zone = pe.pitchData?.zone;
    const isBall = pe.details?.isBall;
    const isInPlay = pe.details?.isInPlay;
    const isStrike = pe.details?.isStrike;
    const callDesc = pe.details?.call?.description || '';
    const typeCode = pe.details?.type?.code || '??';
    const speed = pe.pitchData?.startSpeed;
    const spinRate = pe.pitchData?.breaks?.spinRate;

    let color = '#9CA3AF';
    if (isBall) color = '#22c55e';
    else if (isInPlay) color = '#3B82F6';
    else if (isStrike) color = '#ef4444';

    pitchList.push({
      number: pe.pitchNumber,
      pX: pX != null ? pX : null,
      pZ: pZ != null ? pZ : null,
      zone,
      typeCode,
      typeName: PITCH_TYPE_MAP[typeCode] || typeCode,
      speed: speed != null ? Math.round(speed) : null,
      spinRate: spinRate != null ? Math.round(spinRate) : null,
      callDesc,
      color
    });
  });

  if (pitchList.length === 0) {
    return <div style={{ padding: '6px 8px', fontSize: large ? '0.8rem' : '0.65rem', color: '#9CA3AF' }}>No pitch data available.</div>;
  }

  // Strike zone boundaries (feet)
  const szL = -0.708, szR = 0.708, szB = 1.5, szT = 3.5;
  // Display window
  const xMin = -1.8, xMax = 1.8, yMin = 0.5, yMax = 4.5;
  // SVG size — large mode for matchup detail pane
  const svgW = large ? 200 : 130, svgH = large ? 240 : 155, pad = large ? 8 : 6;
  const plotW = svgW - 2 * pad, plotH = svgH - 2 * pad;
  // Coordinate mappers
  const mx = (x) => pad + ((x - xMin) / (xMax - xMin)) * plotW;
  const my = (z) => pad + ((yMax - z) / (yMax - yMin)) * plotH;
  const dotR = large ? 10 : 7;
  const dotFontSize = large ? '10' : '7';
  const dotTextOffset = large ? 3.5 : 2.5;
  const listFontSize = large ? '0.75rem' : '0.6rem';

  return (
    <div style={{ display: 'flex', flexDirection: isMobileView ? 'column' : 'row', gap: large ? '16px' : '12px', padding: large ? '12px 10px' : '8px 6px', alignItems: isMobileView ? 'center' : 'flex-start' }}>
      {/* SVG strike zone plot */}
      <svg width={svgW} height={svgH} style={{ flexShrink: 0 }}>
        <rect x="0" y="0" width={svgW} height={svgH} fill="rgba(15,15,25,0.6)" rx="4" />

        {/* Strike zone box */}
        <rect
          x={mx(szL)} y={my(szT)}
          width={mx(szR) - mx(szL)} height={my(szB) - my(szT)}
          fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.3)" strokeWidth="1"
        />

        {/* Zone gridlines (3x3) */}
        {[1, 2].map(i => (
          <React.Fragment key={i}>
            <line
              x1={mx(szL + (szR - szL) * i / 3)} y1={my(szT)}
              x2={mx(szL + (szR - szL) * i / 3)} y2={my(szB)}
              stroke="rgba(255,255,255,0.12)" strokeWidth="0.5"
            />
            <line
              x1={mx(szL)} y1={my(szB + (szT - szB) * i / 3)}
              x2={mx(szR)} y2={my(szB + (szT - szB) * i / 3)}
              stroke="rgba(255,255,255,0.12)" strokeWidth="0.5"
            />
          </React.Fragment>
        ))}

        {/* Home plate (pentagon at bottom) */}
        {(() => {
          const pw = 0.708; // half-plate in feet
          const py = yMin + 0.15;
          return (
            <polygon
              points={`${mx(-pw)},${my(py)} ${mx(-pw)},${my(py - 0.12)} ${mx(0)},${my(py - 0.22)} ${mx(pw)},${my(py - 0.12)} ${mx(pw)},${my(py)}`}
              fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.7"
            />
          );
        })()}

        {/* Pitch dots */}
        {pitchList.map(p => {
          let cx, cy;
          if (p.pX != null && p.pZ != null) {
            cx = mx(p.pX);
            cy = my(p.pZ);
          } else if (p.zone && ZONE_CENTERS[p.zone]) {
            const [zx, zy] = ZONE_CENTERS[p.zone];
            cx = mx(zx);
            cy = my(zy);
          } else {
            return null;
          }
          // Clamp to SVG bounds
          cx = Math.max(pad + dotR, Math.min(svgW - pad - dotR, cx));
          cy = Math.max(pad + dotR, Math.min(svgH - pad - dotR, cy));
          return (
            <g key={p.number}>
              <circle cx={cx} cy={cy} r={dotR} fill={p.color} opacity={0.85} />
              <text x={cx} y={cy + dotTextOffset} textAnchor="middle" fill="white" fontSize={dotFontSize} fontWeight="700" style={{ pointerEvents: 'none' }}>
                {p.number}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Pitch list */}
      <div style={{ flex: 1, minWidth: 0, fontSize: listFontSize }}>
        <div style={{ display: 'flex', gap: '4px', padding: '2px 0', borderBottom: '1px solid #333', marginBottom: '2px' }}>
          <span style={{ width: large ? '20px' : '16px', color: '#6b7280', fontWeight: 600, textAlign: 'center' }}>#</span>
          <span style={{ width: large ? '100px' : '42px', color: '#6b7280', fontWeight: 600 }}>Type</span>
          <span style={{ width: large ? '36px' : '28px', color: '#6b7280', fontWeight: 600, textAlign: 'right' }}>MPH</span>
          <span style={{ width: large ? '40px' : '32px', color: '#6b7280', fontWeight: 600, textAlign: 'right' }}>Spin</span>
          <span style={{ color: '#6b7280', fontWeight: 600, marginLeft: '4px' }}>Result</span>
        </div>
        {pitchList.map(p => (
          <div key={p.number} style={{ display: 'flex', gap: '4px', padding: large ? '2px 0' : '1px 0', color: p.color }}>
            <span style={{ width: large ? '20px' : '16px', textAlign: 'center', fontWeight: 600 }}>{p.number}</span>
            <span style={{ width: large ? '100px' : '42px', whiteSpace: 'nowrap' }}>{p.typeName}</span>
            <span style={{ width: large ? '36px' : '28px', textAlign: 'right' }}>{p.speed || '-'}</span>
            <span style={{ width: large ? '40px' : '32px', textAlign: 'right', color: '#9CA3AF' }}>{p.spinRate || '-'}</span>
            <span style={{ marginLeft: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.callDesc}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Season-level pitch-zone heatmap driven by seasonStats.byZone. Renders a 3x3
// strike zone grid surrounded by 4 chase-zone corners (Statcast zones 11-14).
// Each cell shows the AVG on batted balls (H / BBE) and the pitch count seen
// in that zone, color-coded from cold blue → hot red (batter perspective).
const ZoneHeatmap = ({ byZone, pitchLabel, isPitcher = false, metric = 'avg' }) => {
  const zones = {};
  // Detect data shape — pitcher zone counts store `thrown` instead of `seen`
  // and skip swings/whiffs entirely.
  let hasSwingsData = false;
  Object.entries(byZone || {}).forEach(([key, s]) => {
    const bbe = s.bbe || 0;
    const h = s.h || 0;
    const swings = s.swings || 0;
    const whiffs = s.whiffs || 0;
    // Prefer stored fouls when the aggregator provides it (foul tips double-
    // count as both whiffs and fouls, so the identity swings - whiffs - bbe
    // undercounts). Fall back to the identity for older docs that predate
    // per-zone fouls.
    const fouls = s.fouls != null
      ? s.fouls
      : (swings > 0 ? Math.max(0, swings - whiffs - bbe) : 0);
    if (swings > 0 || whiffs > 0 || fouls > 0) hasSwingsData = true;
    zones[key] = {
      pitches: s.seen || s.thrown || 0,
      swings,
      whiffs,
      fouls,
      bbe,
      h,
      hr: s.hr || 0,
      k: s.k || 0,
      pa: s.pa || 0,
      avg: bbe > 0 ? h / bbe : null,
    };
  });
  const totalSeen = Object.values(zones).reduce((sum, z) => sum + z.pitches, 0);
  if (totalSeen === 0) return null;
  // Tooltip adapts to the available fields. Batter zones show swings/whiffs/
  // fouls too; pitcher zones only surface pitches thrown, BBE, hits, HR, AVG.
  const zoneTip = (zoneKey, z) => {
    if (!z || !z.pitches) return `Zone ${zoneKey} · No pitches`;
    const avgStr = z.avg == null ? '-' : z.avg.toFixed(3).replace(/^0\./, '.');
    const seenPct = totalSeen > 0 ? Math.round((z.pitches / totalSeen) * 100) : 0;
    const lines = [`Zone ${zoneKey}`, `${isPitcher ? 'Thrown' : 'Seen'}: ${z.pitches} (${seenPct}%)`];
    if (hasSwingsData) {
      // Same reconciliation as the radial: back out foul tips (double-
      // counted in whiffs + fouls) so swings = whiffs + fouls + bbe.
      const foulsAdj = Math.max(0, z.swings - z.whiffs - z.bbe);
      const swingPct = z.pitches > 0 ? Math.round((z.swings / z.pitches) * 100) : 0;
      const foulPct = z.swings > 0 ? Math.round((foulsAdj / z.swings) * 100) : 0;
      const whiffPct = z.swings > 0 ? Math.round((z.whiffs / z.swings) * 100) : 0;
      lines.push(`Swings: ${z.swings} (${swingPct}%)`, `Fouls: ${foulsAdj} (${foulPct}%)`, `Whiffs: ${z.whiffs} (${whiffPct}%)`);
    }
    const bbePct = z.swings > 0 ? Math.round((z.bbe / z.swings) * 100) : 0;
    lines.push(`BBE: ${z.bbe}${hasSwingsData ? ` (${bbePct}%)` : ''}`);
    const hitPct = z.bbe > 0 ? Math.round((z.h / z.bbe) * 100) : 0;
    lines.push(`Hits: ${z.h}${z.bbe > 0 ? ` (${hitPct}%)` : ''}`);
    if (z.hr > 0) {
      const hrPct = z.bbe > 0 ? Math.round((z.hr / z.bbe) * 100) : 0;
      lines.push(`HR: ${z.hr}${z.bbe > 0 ? ` (${hrPct}%)` : ''}`);
    }
    lines.push(`AVG: ${avgStr}`);
    return lines.join('\n');
  };

  const fmtAvg = (avg) => avg == null ? '' : avg.toFixed(3).replace(/^0\./, '.');
  const avgFill = (avg) => {
    if (avg == null) return 'rgba(255,255,255,0.04)';
    if (avg >= 0.400) return 'rgba(220, 53, 69, 0.85)';
    if (avg >= 0.325) return 'rgba(220, 53, 69, 0.55)';
    if (avg >= 0.275) return 'rgba(245, 158, 11, 0.55)';
    if (avg >= 0.225) return 'rgba(108, 117, 125, 0.45)';
    if (avg >= 0.175) return 'rgba(13, 110, 253, 0.4)';
    return 'rgba(13, 110, 253, 0.65)';
  };
  // K heatmap: scale zone K count to a red intensity relative to the hottest
  // zone. Empty / zero-K zones fade out, so the strikeout hotspots pop.
  const maxK = Object.values(zones).reduce((m, z) => Math.max(m, z.k || 0), 0);
  const kFill = (k) => {
    if (!k || maxK === 0) return 'rgba(255,255,255,0.04)';
    const ratio = k / maxK;
    // Ramp from a dim red (0.25 alpha) to a hot red (0.85 alpha).
    const alpha = 0.25 + 0.6 * ratio;
    return `rgba(220, 53, 69, ${alpha.toFixed(3)})`;
  };
  const isK = metric === 'k';
  const fillFor = (z) => isK ? kFill(z?.k || 0) : avgFill(z?.avg);
  const primaryText = (z) => {
    if (!z) return '';
    if (isK) return String(z.k || 0);
    return fmtAvg(z.avg) || '-';
  };
  const secondaryText = (z) => {
    if (!z) return '';
    if (isK) {
      if (!z.pa) return '';
      const rate = Math.round((z.k / z.pa) * 100);
      return `${rate}% PA`;
    }
    return hasSwingsData ? `${z.swings} sw` : `${z.pitches} p`;
  };

  // Layout in a 100x100 viewBox: chase quadrants tile the whole canvas
  // (0-50 / 50-100 in each axis). The strike zone then sits on top from
  // 20-80 (3x3 grid, cells 20px each) so each chase corner remains visible
  // as an L-shape wrapping the SZ corner. Chase-zone labels go in the outer
  // corner where no overlap occurs.
  const quadW = 50;
  const corners = [
    { zone: '11', x: 0, y: 0, labelX: 10, labelY: 10 },
    { zone: '12', x: quadW, y: 0, labelX: 90, labelY: 10 },
    { zone: '13', x: 0, y: quadW, labelX: 10, labelY: 90 },
    { zone: '14', x: quadW, y: quadW, labelX: 90, labelY: 90 },
  ];
  const szMin = 16, szMax = 84, sz = szMax - szMin, cell = sz / 3;
  const szCells = [
    { zone: '1', row: 0, col: 0 }, { zone: '2', row: 0, col: 1 }, { zone: '3', row: 0, col: 2 },
    { zone: '4', row: 1, col: 0 }, { zone: '5', row: 1, col: 1 }, { zone: '6', row: 1, col: 2 },
    { zone: '7', row: 2, col: 0 }, { zone: '8', row: 2, col: 1 }, { zone: '9', row: 2, col: 2 },
  ];

  const title = isK
    ? `Pitch Zone — Strikeouts${pitchLabel ? ` · ${pitchLabel}` : ''}`
    : `Pitch Zone — AVG on Contact${pitchLabel ? ` · ${pitchLabel}` : ''}`;
  const legend = isK
    ? "catcher's view · red = more K's"
    : "catcher's view · red hot / blue cold";

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: '0.65rem', color: '#9CA3AF', marginBottom: '4px', fontWeight: 600 }}>
        {title}
      </div>
      <svg width={240} height={240} viewBox="0 0 100 100" style={{ display: 'block' }}>
        {corners.map(({ zone, x, y, labelX, labelY }) => {
          const z = zones[zone];
          return (
            <g key={zone}>
              <title>{zoneTip(zone, z)}</title>
              <rect x={x} y={y} width={quadW} height={quadW} fill={fillFor(z)} stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
              {z && z.pitches > 0 && (
                <>
                  <text x={labelX} y={labelY - 1} textAnchor="middle" fill="white" fontSize="5" fontWeight="700" style={{ pointerEvents: 'none' }}>{primaryText(z)}</text>
                  <text x={labelX} y={labelY + 5} textAnchor="middle" fill="rgba(255,255,255,0.65)" fontSize="3.5" style={{ pointerEvents: 'none' }}>{secondaryText(z)}</text>
                </>
              )}
            </g>
          );
        })}
        <rect x={szMin} y={szMin} width={sz} height={sz} fill="rgba(0,0,0,0.4)" stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
        {szCells.map(({ zone, row, col }) => {
          const z = zones[zone];
          const rx = szMin + col * cell;
          const ry = szMin + row * cell;
          const cx = rx + cell / 2;
          const cy = ry + cell / 2;
          return (
            <g key={zone}>
              <title>{zoneTip(zone, z)}</title>
              <rect x={rx} y={ry} width={cell} height={cell} fill={fillFor(z)} stroke="rgba(255,255,255,0.22)" strokeWidth="0.3" />
              {z && z.pitches > 0 && (
                <>
                  <text x={cx} y={cy - 1} textAnchor="middle" fill="white" fontSize="6" fontWeight="700" style={{ pointerEvents: 'none' }}>{primaryText(z)}</text>
                  <text x={cx} y={cy + 5} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="4" style={{ pointerEvents: 'none' }}>{secondaryText(z)}</text>
                </>
              )}
            </g>
          );
        })}
      </svg>
      <div style={{ fontSize: '0.55rem', color: '#6c757d', marginTop: '2px' }}>{legend}</div>
    </div>
  );
};

// Radial "Pitch Performance" chart — Chart.js PolarArea. One wedge per pitch
// type (top 6 by pitches seen this season). Wedge radius maps the batter's
// AVG on that pitch to a 0-100 score via piecewise-linear MLB thresholds
// (.200 → 0, .275 → 50, .350 → 100). Wedge color scales red → orange → green
// as AVG rises. Angular spacing is uniform (Chart.js gives equal-angle
// slices for free) and adapts to pitch count so a batter with 3-4 pitches
// doesn't leave empty slots. A center black overlay creates the donut hole.
const PITCH_NAMES = { FF: '4-Seam', SI: 'Sinker', FC: 'Cutter', CU: 'Curve', SL: 'Slider', CH: 'Change', ST: 'Sweeper', KC: 'Knuckle CB', FS: 'Splitter', KN: 'Knuckle' };

const PitchPerformanceRadial = ({ byPitch, selectedCode, onPitchClick, isPitcher = false }) => {
  const pitches = Object.entries(byPitch || {})
    .map(([code, d]) => {
      const seen = d.seen || d.thrown || 0;
      const ab = d.ab || d.bbe || 0;
      const h = d.h || 0;
      const swings = d.swings || 0;
      const whiffs = d.whiffs || 0;
      const fouls = d.fouls || 0;
      const bbe = d.bbe || 0;
      return {
        code,
        name: PITCH_NAMES[code] || code,
        seen,
        swings,
        whiffs,
        fouls,
        bbe,
        avg: ab > 0 ? h / ab : null,
        whiffPct: swings > 0 ? whiffs / swings : null,
      };
    })
    .filter(p => p.seen > 0)
    .sort((a, b) => b.seen - a.seen)
    .slice(0, 6);
  if (pitches.length === 0) return null;
  const totalSeen = Object.values(byPitch || {}).reduce((sum, d) => sum + (d.seen || d.thrown || 0), 0);

  // Wedge size = batted-ball-event count. Color mirrors the app-wide AVG
  // badge tiers used in Matchups / BvP: elite green ≥ .350, hot orange ≥
  // .300, dim orange ≥ .275, blue below that.
  const colorForAvg = (avg) => {
    if (avg == null) return 'rgba(59, 130, 246, 0.4)';     // dim blue — no data
    if (avg >= 0.350) return 'rgba(0, 128, 0, 0.9)';       // "green" — elite
    if (avg >= 0.300) return 'rgba(245, 158, 11, 0.9)';    // #F59E0B — hot
    if (avg >= 0.275) return 'rgba(245, 158, 11, 0.55)';   // dim orange — warm
    return 'rgba(59, 130, 246, 0.75)';                     // #3B82F6 — cold
  };

  const bbeValues = pitches.map(p => p.bbe);
  const maxBbe = Math.max(1, ...bbeValues);
  // Floor at 1 so a zero-BBE pitch still shows a slim line rather than
  // vanishing entirely. Tooltip reports the true count.
  const renderBbe = bbeValues.map(b => Math.max(1, b));
  const data = {
    labels: pitches.map(p => p.name),
    datasets: [{
      data: renderBbe,
      backgroundColor: pitches.map(p => colorForAvg(p.avg)),
      borderColor: pitches.map(p => p.code === selectedCode ? '#ffc107' : 'rgba(0,0,0,0.35)'),
      borderWidth: pitches.map(p => p.code === selectedCode ? 3 : 1),
    }],
  };

  const options = {
    responsive: false,
    maintainAspectRatio: false,
    onClick: (_event, elements) => {
      if (!onPitchClick || !elements || elements.length === 0) return;
      const idx = elements[0].index;
      const code = pitches[idx].code;
      onPitchClick(code === selectedCode ? null : code);
    },
    scales: {
      r: {
        min: 0,
        max: maxBbe,
        ticks: { display: false, backdropColor: 'transparent' },
        grid: { color: 'rgba(255,255,255,0.06)' },
        angleLines: { color: 'rgba(255,255,255,0.08)' },
        pointLabels: {
          display: true,
          centerPointLabels: true,
          color: '#adb5bd',
          font: { size: 10, weight: '600' },
          callback: (label, index) => {
            const p = pitches[index];
            const display = p.avg == null ? '-' : p.avg.toFixed(3).replace(/^0\./, '.');
            return [label, display];
          },
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => pitches[items[0].dataIndex].name,
          label: (ctx) => {
            const p = pitches[ctx.dataIndex];
            const avgStr = p.avg == null ? '-' : p.avg.toFixed(3).replace(/^0\./, '.');
            // Backend `fouls` field includes foul tips, which also count as
            // whiffs — so whiffs + fouls + bbe overshoots swings. Derive
            // non-tip fouls from the identity so the three buckets sum to
            // swings and their rates add to 100%.
            const foulsAdj = Math.max(0, p.swings - p.whiffs - p.bbe);
            const seenPct = totalSeen > 0 ? Math.round((p.seen / totalSeen) * 100) : 0;
            const swingPct = p.seen > 0 ? Math.round((p.swings / p.seen) * 100) : 0;
            const foulPct = p.swings > 0 ? Math.round((foulsAdj / p.swings) * 100) : 0;
            const whiffPct = p.swings > 0 ? Math.round((p.whiffs / p.swings) * 100) : 0;
            const bbePct = p.swings > 0 ? Math.round((p.bbe / p.swings) * 100) : 0;
            return [
              `${isPitcher ? 'Thrown' : 'Seen'}: ${p.seen} (${seenPct}%)`,
              `AVG: ${avgStr}`,
              `Swings: ${p.swings} (${swingPct}%)`,
              `Fouls: ${foulsAdj} (${foulPct}%)`,
              `Whiffs: ${p.whiffs} (${whiffPct}%)`,
              `BBE: ${p.bbe} (${bbePct}%)`,
            ];
          },
        },
      },
    },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: '0.65rem', color: '#9CA3AF', marginBottom: '4px', fontWeight: 600 }}>
        Pitch Performance — AVG per Pitch
      </div>
      <div style={{ width: 240, height: 240 }}>
        <PolarArea data={data} options={options} width={240} height={240} />
      </div>
    </div>
  );
};

// Season-level pitch-speed distribution driven by seasonStats.byPitchSpeed
// (keys like "FF_100plus"). Aggregates across all pitch types into 5 velocity
// buckets and renders horizontal bars with AVG-on-contact + whiff% overlays.
const SpeedDistribution = ({ byPitchSpeed }) => {
  const empty = () => ({ seen: 0, bbe: 0, h: 0, swings: 0, whiffs: 0 });
  const buckets = {
    sub90: empty(),
    '90to94': empty(),
    '95to97': empty(),
    '98to99': empty(),
    '100plus': empty(),
  };
  Object.entries(byPitchSpeed || {}).forEach(([key, s]) => {
    const idx = key.lastIndexOf('_');
    const bucket = idx > 0 ? key.slice(idx + 1) : key;
    if (!buckets[bucket]) return;
    buckets[bucket].seen += s.seen || 0;
    buckets[bucket].bbe += s.bbe || 0;
    buckets[bucket].h += s.h || 0;
    buckets[bucket].swings += s.swings || 0;
    buckets[bucket].whiffs += s.whiffs || 0;
  });
  const labels = { sub90: '< 90', '90to94': '90-94', '95to97': '95-97', '98to99': '98-99', '100plus': '100+' };
  const order = ['sub90', '90to94', '95to97', '98to99', '100plus'];
  const totalSwings = order.reduce((sum, k) => sum + buckets[k].swings, 0);
  if (totalSwings === 0) return null;
  const maxSwings = Math.max(1, ...order.map(k => buckets[k].swings));

  const barColor = (avg) => {
    if (avg == null) return '#374151';
    if (avg >= 0.325) return '#dc3545';
    if (avg >= 0.275) return '#F59E0B';
    if (avg >= 0.225) return '#6c757d';
    return '#3B82F6';
  };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '0.65rem', color: '#9CA3AF', marginBottom: '4px', fontWeight: 600 }}>
        Pitch Speed (mph)
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {order.map(k => {
          const b = buckets[k];
          const pct = totalSwings > 0 ? (b.swings / totalSwings) * 100 : 0;
          const width = (b.swings / maxSwings) * 100;
          const avg = b.bbe > 0 ? b.h / b.bbe : null;
          const whiff = b.swings > 0 ? b.whiffs / b.swings : null;
          return (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.65rem' }}>
              <div style={{ width: '38px', color: '#9CA3AF', flexShrink: 0 }}>{labels[k]}</div>
              <div style={{ flex: 1, position: 'relative', height: '14px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${width}%`, backgroundColor: barColor(avg), opacity: 0.7 }} />
                <div style={{ position: 'absolute', left: '4px', top: '50%', transform: 'translateY(-50%)', color: 'white', fontSize: '0.55rem', fontWeight: 600 }}>
                  {b.swings} sw ({Math.round(pct)}%)
                </div>
              </div>
              <div style={{ minWidth: '110px', color: '#dee2e6', fontSize: '0.6rem', textAlign: 'right', flexShrink: 0 }}>
                {avg != null ? `${avg.toFixed(3).replace(/^0\./, '.')} AVG` : '- AVG'}
                {whiff != null ? ` · ${Math.round(whiff * 100)}% Whiff` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Ball flight iframe viewer — positioned absolute within parent container
const BallFlightViewer = ({ playId, season, onClose, topOffset }) => {
  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
  return (
  <div style={{ position: 'fixed', top: isMobileView ? 0 : `${topOffset || 0}px`, left: 0, right: isMobileView ? 0 : '50%', bottom: 0, backgroundColor: 'rgba(0,0,0,0.98)', zIndex: 9999, display: 'flex', flexDirection: 'column', borderRadius: isMobileView ? 0 : '12px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', flexShrink: 0, borderBottom: '1px solid #333', backgroundColor: '#1a1d21' }}>
      <span style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>Ball Flight</span>
      <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '24px', cursor: 'pointer', padding: '0 8px', lineHeight: 1 }}>×</button>
    </div>
    <iframe
      src={`/ball-flight/embed?playId=${playId}&season=${season}`}
      style={{ flex: 1, border: 'none' }}
      title="Ball Flight"
    />
  </div>
  );
};

// Play button for at-bat ball flight
const PlayButton = ({ ab, onClick }) => {
  const gamePk = ab.gamePk || ab.game_pk;
  const atBatIndex = ab.atBatIndex ?? ab.at_bat_index;
  if (!gamePk || atBatIndex == null) return null;
  // Only show for balls in play
  const hasHitData = ab.playEvents?.some(pe => pe.hitData);
  if (!hasHitData) return null;
  return (
    <span
      style={{ cursor: 'pointer', color: '#6c757d', fontSize: '0.7rem', marginLeft: '4px', verticalAlign: 'middle' }}
      onClick={(e) => { e.stopPropagation(); onClick(`${gamePk}_${atBatIndex}`, ab.officialDate ? ab.officialDate.slice(0, 4) : String(new Date().getFullYear())); }}
      title="Ball flight"
    >&#x25B6;</span>
  );
};

// Extract a display surname, keeping a trailing suffix like Jr./Sr./II/III/IV/V
// so names like "Bobby Witt Jr." render as "Witt Jr." instead of just "Jr."
const NAME_SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v']);
const getSurname = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return '';
  const last = parts[parts.length - 1];
  if (parts.length >= 2 && NAME_SUFFIXES.has(last.toLowerCase())) {
    return `${parts[parts.length - 2]} ${last}`;
  }
  return last;
};

// At-bats detail component rendered inside the expanded game log row
const AtBatsDetail = ({ data, isPitcher, onBallFlight, mobile }) => {
  const [expandedPitchIdx, setExpandedPitchIdx] = useState(null);
  const atBats = data.atBats;

  if (!atBats || !Array.isArray(atBats) || atBats.length === 0) {
    return (
      <div style={{ padding: '6px 8px', fontSize: '0.65rem', color: '#9CA3AF' }}>
        No at-bat data available.
      </div>
    );
  }

  if (mobile) {
    return (
      <div style={{ padding: '2px 0' }}>
        {atBats.map((ab, i) => {
          const event = ab.result?.event || '-';
          const inning = ab.inning || '-';
          const rbi = ab.result?.rbi || 0;
          const count = ab.finalCount ? `${ab.finalCount.balls}-${ab.finalCount.strikes}` : '-';
          const matchupName = isPitcher
            ? (getSurname(ab.batter?.fullName) || '-')
            : (getSurname(ab.pitcher?.fullName) || '-');
          const matchupHand = isPitcher ? (ab.batterSide || '') : (ab.pitcherHand || '');
          const lastPitchEvent = ab.playEvents?.[ab.playEvents.length - 1];
          const lastPitch = (lastPitchEvent?.details?.type?.description || lastPitchEvent?.pitchType || '-').replace(/^Four-Seam/, '4-Seam');
          const velo = lastPitchEvent?.pitchData?.startSpeed ?? lastPitchEvent?.startSpeed ?? null;
          const spin = lastPitchEvent?.pitchData?.breaks?.spinRate ?? lastPitchEvent?.spinRate ?? null;
          const hitEvent = ab.playEvents?.find(pe => pe.hitData);
          const hitData = hitEvent?.hitData;
          const ev = hitData?.launchSpeed != null ? `${hitData.launchSpeed}` : '';
          const la = hitData?.launchAngle != null ? `${hitData.launchAngle}\u00B0` : '';
          const dist = hitData?.totalDistance != null ? `${hitData.totalDistance}'` : '';
          const evNum = ev ? parseFloat(ev) : null;
          const laNum = hitData?.launchAngle;
          const isBarrel = (() => {
            if (evNum == null || laNum == null) return false;
            if (evNum < 98) return false;
            const minLA = Math.max(8, 26 - (evNum - 98));
            const maxLA = Math.min(50, 30 + (evNum - 98));
            return laNum >= minLA && laNum <= maxLA;
          })();
          const isHardHit = !isBarrel && evNum != null && evNum >= 95;
          const isHit = /single|double|triple/i.test(event) && !/home run/i.test(event);
          const isHR = /home run/i.test(event);
          const isWalk = /walk|hit by pitch|intentional/i.test(event);
          const isOut = !isHit && !isHR && !isWalk && event !== '-';
          const resultColor = isHR ? '#ffc107' : isHit ? '#22c55e' : isWalk ? '#60a5fa' : isOut ? '#9CA3AF' : '#fff';
          const hasPitches = ab.playEvents?.length > 0;
          const isExpanded = expandedPitchIdx === i;
          const ordinal = (() => { const n = parseInt(inning); if (isNaN(n)) return inning; const s = ['th','st','nd','rd']; const v = n % 100; return n + (s[(v-20)%10] || s[v] || s[0]); })();

          return (
            <React.Fragment key={i}>
              <div
                style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', cursor: hasPitches ? 'pointer' : 'default', backgroundColor: isExpanded ? 'rgba(96,165,250,0.18)' : (i % 2 === 0 ? '#1e2228' : '#0f1115'), gap: '8px' }}
                onClick={() => hasPitches && setExpandedPitchIdx(isExpanded ? null : i)}
              >
                {/* Col 1: Inning + Pitcher */}
                <div style={{ width: '52px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div style={{ color: '#9CA3AF', fontSize: '0.8rem', fontWeight: 500 }}>{ordinal}</div>
                  <div style={{ color: '#6b7280', fontSize: '0.7rem' }}>{matchupName}{matchupHand ? ` (${matchupHand})` : ''}</div>
                </div>

                {/* Col 2: Pitch / Velo+Spin */}
                <div style={{ flex: 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                  <div style={{ color: '#dee2e6', fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center', width: '100%' }}>{lastPitch}</div>
                  <div style={{ display: 'flex', gap: '10px', fontSize: '0.72rem', color: '#9CA3AF', justifyContent: 'center' }}>
                    {velo != null && <span><span style={{ color: '#6b7280' }}>Velo</span> {Math.round(velo)}</span>}
                    {spin != null && <span><span style={{ color: '#6b7280' }}>Spin</span> {spin}</span>}
                  </div>
                </div>

                {/* Col 3: Count */}
                <div style={{ width: '32px', flexShrink: 0, textAlign: 'center', color: hasPitches ? '#93c5fd' : '#fff', fontSize: '0.85rem', fontWeight: 500 }}>{count}</div>

                {/* Col 4: Distance */}
                <div style={{ width: '44px', flexShrink: 0, textAlign: 'center', color: '#9CA3AF', fontSize: '0.8rem' }}>{dist || '-'}</div>

                {/* Col 5: Result */}
                <div style={{ width: '90px', flexShrink: 0, textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                  {isBarrel ? (
                    <span style={{ backgroundColor: 'green', color: 'yellow', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.88rem' }}>{event}</span>
                  ) : isHardHit ? (
                    <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '0.88rem' }}>{event}</span>
                  ) : (
                    <span style={{ color: resultColor, fontWeight: 700, fontSize: '0.92rem' }}>{event}</span>
                  )}
                  {rbi > 0 && <span style={{ color: '#F59E0B', fontSize: '0.65rem' }}>{rbi} RBI</span>}
                  {(ev || la) && !isBarrel && !isHardHit && (
                    <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                      {ev && `${ev}`}{la && ` · ${la}`}
                    </span>
                  )}
                </div>
              </div>
              {isExpanded && (
                <div style={{ border: '1px solid #333', backgroundColor: 'rgba(20,20,30,0.95)' }}>
                  {(() => {
                    const gamePk = ab.gamePk || ab.game_pk;
                    const atBatIndex = ab.atBatIndex ?? ab.at_bat_index;
                    const hasHitData = ab.playEvents?.some(pe => pe.hitData);
                    if (!gamePk || atBatIndex == null || !hasHitData) return null;
                    const season = ab.officialDate ? ab.officialDate.slice(0, 4) : String(new Date().getFullYear());
                    return (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 12px' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); onBallFlight && onBallFlight({ playId: `${gamePk}_${atBatIndex}`, season }); }}
                          style={{ backgroundColor: '#2a2d31', color: '#fff', border: '1px solid #444', borderRadius: '6px', padding: '8px 20px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <span>&#x25B6;</span> Ball Flight
                        </button>
                      </div>
                    );
                  })()}
                  <PitchZoneGrid pitchEvents={ab.playEvents} large mobile />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ padding: '4px 6px' }}>
      <table style={{ width: '100%', fontSize: '0.65rem', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={abHeaderStyle}>Inn</th>
            <th style={{ ...abHeaderStyle, textAlign: 'left', width: '20%' }}>Result</th>
            <th style={abHeaderStyle}>Count</th>
            <th style={{ ...abHeaderStyle, textAlign: 'left', width: '12%' }}>{isPitcher ? 'Batter' : 'Pitcher'}</th>
            <th style={abHeaderStyle}>H</th>
            <th style={{ ...abHeaderStyle, textAlign: 'left', width: '12%' }}>Last Pitch</th>
            <th style={abHeaderStyle}>Velo</th>
            <th style={abHeaderStyle}>EV</th>
            <th style={abHeaderStyle}>LA</th>
            <th style={abHeaderStyle}>Dist</th>
            <th style={abHeaderStyle}>BRL</th>
            <th style={abHeaderStyle}></th>
          </tr>
        </thead>
        <tbody>
          {atBats.map((ab, i) => {
            const event = ab.result?.event || '-';
            const inning = ab.inning || '-';
            const rbi = ab.result?.rbi || 0;
            const count = ab.finalCount ? `${ab.finalCount.balls}-${ab.finalCount.strikes}` : '-';
            const matchupName = isPitcher
              ? (getSurname(ab.batter?.fullName) || '-')
              : (getSurname(ab.pitcher?.fullName) || '-');
            const matchupHand = isPitcher ? (ab.batterSide || '-') : (ab.pitcherHand || '-');
            const lastPitchEvent = ab.playEvents?.[ab.playEvents.length - 1];
            const velo = lastPitchEvent?.pitchData?.startSpeed ?? lastPitchEvent?.startSpeed ?? null;
            const lastPitch = (lastPitchEvent?.details?.type?.description || lastPitchEvent?.pitchType || '-').replace(/^Four-Seam/, '4-Seam');
            // hitData is nested inside playEvents — find the event that has it
            const hitEvent = ab.playEvents?.find(pe => pe.hitData);
            const hitData = hitEvent?.hitData;
            const ev = hitData?.launchSpeed != null ? `${hitData.launchSpeed}` : '';
            const la = hitData?.launchAngle != null ? `${hitData.launchAngle}\u00B0` : '';
            const dist = hitData?.totalDistance != null ? `${hitData.totalDistance}'` : '';
            // Barrel: use API flag, fallback to Statcast definition (EV >= 98, LA in expanding zone)
            const isBarrel = ab.isBarrel || (() => {
              if (!hitData?.launchSpeed || !hitData?.launchAngle) return false;
              const speed = hitData.launchSpeed;
              const angle = hitData.launchAngle;
              if (speed < 98) return false;
              const minLA = Math.max(8, 26 - (speed - 98));
              const maxLA = Math.min(50, 30 + (speed - 98));
              return angle >= minLA && angle <= maxLA;
            })();
            const isHit = /single|double|triple|home run/i.test(event);
            const isOut = /out|strikeout|flyout|groundout|lineout|pop out|foul out|double play|fielder.?s choice/i.test(event) && !isHit;
            const isWalk = /walk|hit by pitch|intentional/i.test(event);

            const hasPitches = ab.playEvents?.length > 0;
            return (
              <React.Fragment key={i}>
                <tr style={{ cursor: hasPitches ? 'pointer' : 'default' }} onClick={() => hasPitches && setExpandedPitchIdx(expandedPitchIdx === i ? null : i)}>
                  <td style={abCellStyle}>{inning}</td>
                  <td style={{
                    ...abCellStyle,
                    textAlign: 'left',
                    fontWeight: isHit ? 'bold' : 'normal',
                    color: isHit ? '#22c55e' : isWalk ? '#60a5fa' : isOut ? '#9CA3AF' : 'white'
                  }}>
                    {event}{rbi > 0 ? <span style={{ color: '#F59E0B', marginLeft: '4px' }}>{rbi} RBI</span> : ''}
                  </td>
                  <td
                    style={{
                      ...abCellStyle,
                      cursor: ab.playEvents?.length > 0 ? 'pointer' : undefined,
                      color: expandedPitchIdx === i ? '#60a5fa' : ab.playEvents?.length > 0 ? '#93c5fd' : 'white',
                      textDecoration: ab.playEvents?.length > 0 ? 'underline' : undefined,
                      textDecorationColor: ab.playEvents?.length > 0 ? '#4b5563' : undefined
                    }}
                    onClick={() => ab.playEvents?.length > 0 && setExpandedPitchIdx(expandedPitchIdx === i ? null : i)}
                  >{count}</td>
                  <td style={{ ...abCellStyle, textAlign: 'left', color: '#9CA3AF' }}>{matchupName}</td>
                  <td style={{ ...abCellStyle, color: '#6c757d' }}>{matchupHand}</td>
                  <td style={{ ...abCellStyle, textAlign: 'left', color: '#9CA3AF' }}>{lastPitch}</td>
                  <td style={{ ...abCellStyle, color: '#9CA3AF' }}>{velo != null ? Math.round(velo) : '-'}</td>
                  <td style={{ ...abCellStyle, color: ev && parseFloat(ev) >= 95 ? '#F59E0B' : '#9CA3AF' }}>{ev || '-'}</td>
                  <td style={abCellStyle}>{la || '-'}</td>
                  <td style={abCellStyle}>{dist || '-'}</td>
                  <td style={abCellStyle}>
                    {isBarrel ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 2L5 5M5 5L3.5 6.5C3 7 3 7.8 3.5 8.3L15.7 20.5C16.2 21 17 21 17.5 20.5L20.5 17.5C21 17 21 16.2 20.5 15.7L8.3 3.5C7.8 3 7 3 6.5 3.5L5 5Z"/>
                      </svg>
                    ) : hitData ? (
                      <span style={{ color: '#4b5563' }}>-</span>
                    ) : ''}
                  </td>
                  <td style={abCellStyle}><PlayButton ab={ab} onClick={(playId, season) => onBallFlight && onBallFlight({ playId, season })} /></td>
                </tr>
                {expandedPitchIdx === i && (
                  <tr>
                    <td colSpan={12} style={{ padding: 0, border: '1px solid #333', backgroundColor: 'rgba(20,20,30,0.95)' }}>
                      <PitchZoneGrid pitchEvents={ab.playEvents} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const abHeaderStyle = {
  color: '#6b7280',
  padding: '2px 4px',
  borderBottom: '1px solid #333',
  textAlign: 'center',
  fontSize: '0.6rem',
  fontWeight: 600
};

const abCellStyle = {
  padding: '2px 4px',
  borderBottom: '1px solid rgba(51,51,51,0.5)',
  textAlign: 'center',
  color: 'white',
  fontSize: '0.65rem'
};

const MatchupStatRow = ({ s, label, rowStyle, onClick }) => {
  const c = { padding: '4px 3px', textAlign: 'center' };
  const clr = rowStyle?.color || '#dee2e6';
  return (
    <tr style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', ...rowStyle }} onClick={onClick}>
      <td style={{ ...c, textAlign: 'left', color: rowStyle?.color || '#adb5bd', fontWeight: rowStyle?.fontWeight || 400, fontSize: '0.65rem' }}>{label}</td>
      <td style={{ ...c, color: clr }}>{s.pa ?? '-'}</td>
      <td style={{ ...c, color: clr }}>{s.h ?? '-'}</td>
      <td style={{ ...c, color: s.hr > 0 ? '#ffc107' : clr }}>{s.hr ?? '-'}</td>
      <td style={{ ...c, color: clr }}>{s.k ?? '-'}</td>
      <td style={{ ...c, color: clr }}>{s.bb ?? '-'}</td>
      <td style={{ ...c, color: s.avg >= 0.300 ? '#28a745' : clr }}>{s.avg != null ? s.avg.toFixed(3).replace(/^0\./, '.') : '-'}</td>
      <td style={{ ...c, color: s.slg >= 0.500 ? '#ffc107' : clr }}>{s.slg != null ? s.slg.toFixed(3).replace(/^0\./, '.') : '-'}</td>
      <td style={{ ...c, color: s.ops >= 0.800 ? '#28a745' : clr }}>{s.ops != null ? s.ops.toFixed(3).replace(/^0\./, '.') : '-'}</td>
      <td style={{ ...c, color: s.barrelRate >= 0.15 ? '#28a745' : clr }}>{s.barrelRate != null ? `${Math.round(s.barrelRate * 100)}%` : '-'}</td>
      <td style={{ ...c, color: s.hardHitRate >= 0.40 ? '#dc3545' : clr }}>{s.hardHitRate != null ? `${Math.round(s.hardHitRate * 100)}%` : '-'}</td>
    </tr>
  );
};

const MatchupAtBatRow = ({ ab, isExpanded, onToggle, onPlayClick }) => {
  const event = ab.result?.event || '-';
  const isHit = /single|double|triple/i.test(event) && !/home run/i.test(event);
  const isHR = /home run/i.test(event);
  const isWalk = /walk|hit by pitch|intentional/i.test(event);
  const isOut = !isHit && !isHR && !isWalk && event !== '-';
  const resultColor = isHR ? '#ffc107' : isHit ? '#22c55e' : isWalk ? '#60a5fa' : isOut ? '#9CA3AF' : '#fff';
  const count = ab.finalCount ? `${ab.finalCount.balls}-${ab.finalCount.strikes}` : '-';
  const lastPitchEvent = ab.playEvents?.[ab.playEvents.length - 1];
  const lastPitch = lastPitchEvent?.details?.type?.description || lastPitchEvent?.pitchType || '-';
  const hitEvent = ab.playEvents?.find(pe => pe.hitData);
  const hitData = hitEvent?.hitData;
  const ev = hitData?.launchSpeed != null ? `${hitData.launchSpeed}` : '';
  const la = hitData?.launchAngle != null ? `${hitData.launchAngle}\u00B0` : '';
  const dist = hitData?.totalDistance != null ? `${hitData.totalDistance}'` : '';
  const dateStr = ab.officialDate ? new Date(ab.officialDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : '-';
  const hasPitches = ab.playEvents?.length > 0;

  return (
    <>
      <tr style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', cursor: hasPitches ? 'pointer' : 'default' }} onClick={() => hasPitches && onToggle()}>
        <td style={{ padding: '3px 4px', textAlign: 'center', color: '#9CA3AF', fontSize: '0.65rem' }}>{dateStr}</td>
        <td style={{ padding: '3px 4px', textAlign: 'left', color: resultColor, fontWeight: isHit || isHR ? 'bold' : 'normal', fontSize: '0.65rem' }}>{event}</td>
        <td style={{ padding: '3px 4px', textAlign: 'center', color: isExpanded ? '#60a5fa' : hasPitches ? '#93c5fd' : '#fff', textDecoration: hasPitches ? 'underline' : 'none', textDecorationColor: '#4b5563', fontSize: '0.65rem' }}>{count}</td>
        <td style={{ padding: '3px 4px', textAlign: 'left', color: '#9CA3AF', fontSize: '0.65rem' }}>{lastPitch}</td>
        <td style={{ padding: '3px 4px', textAlign: 'center', color: ev && parseFloat(ev) >= 95 ? '#F59E0B' : '#9CA3AF', fontSize: '0.65rem' }}>{ev || '-'}</td>
        <td style={{ padding: '3px 4px', textAlign: 'center', color: '#fff', fontSize: '0.65rem' }}>{la || '-'}</td>
        <td style={{ padding: '3px 4px', textAlign: 'center', color: '#fff', fontSize: '0.65rem' }}>{dist || '-'}</td>
        <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: '0.65rem' }}><PlayButton ab={ab} onClick={onPlayClick} /></td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={8} style={{ padding: 0, border: '1px solid #333', backgroundColor: 'rgba(20,20,30,0.95)' }}>
            <PitchZoneGrid pitchEvents={ab.playEvents} large />
          </td>
        </tr>
      )}
    </>
  );
};

const MatchupStatsTable = ({ stats, seasons, batterId, pitcherId, onBallFlight }) => {
  const [expandedYear, setExpandedYear] = useState(null);
  const [atBatsData, setAtBatsData] = useState(null);
  const [atBatsLoading, setAtBatsLoading] = useState(false);
  const [expandedAbIdx, setExpandedAbIdx] = useState(null);

  const seasonData = seasons || {};
  const seasonYears = Object.keys(seasonData).filter(k => k !== 'all').sort((a, b) => b - a);

  const handleYearClick = async (yr) => {
    if (expandedYear === yr) {
      setExpandedYear(null);
      setExpandedAbIdx(null);
      return;
    }
    setExpandedYear(yr);
    setExpandedAbIdx(null);

    // Fetch at-bat data if not already loaded
    if (!atBatsData && batterId && pitcherId) {
      setAtBatsLoading(true);
      try {
        const cleanBatter = String(batterId).replace(/^mlb_/, '');
        const cleanPitcher = String(pitcherId).replace(/^mlb_/, '');
        const res = await fetch(`/api/stats/mlb/atbats/matchup/${cleanBatter}/${cleanPitcher}`);
        if (res.ok) {
          const data = await res.json();
          setAtBatsData(data.atBats || []);
        }
      } catch { /* ignore */ }
      finally { setAtBatsLoading(false); }
    }
  };

  const getYearAtBats = (yr) => {
    if (!atBatsData) return [];
    return atBatsData
      .filter(ab => ab.officialDate && ab.officialDate.startsWith(yr))
      .sort((a, b) => (b.officialDate || '').localeCompare(a.officialDate || ''));
  };

  return (
    <>
    <table style={{ width: '100%', fontSize: '0.7rem', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #333', color: '#adb5bd' }}>
          <th style={{ padding: '4px 3px', textAlign: 'left', fontSize: '0.65rem' }}></th>
          {['PA', 'H', 'HR', 'K', 'BB', 'AVG', 'SLG', 'OPS', 'Brl%', 'HH%'].map(col => (
            <th key={col} style={{ padding: '4px 3px', textAlign: 'center', fontSize: '0.65rem' }}>{col}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {seasonYears.map(yr => {
          const s = seasonData[yr];
          if (!s) return null;
          const isExpanded = expandedYear === yr;
          return (
            <React.Fragment key={yr}>
              <MatchupStatRow
                s={s}
                label={yr}
                rowStyle={{ cursor: 'pointer', backgroundColor: isExpanded ? 'rgba(96,165,250,0.1)' : undefined }}
                onClick={() => handleYearClick(yr)}
              />
              {isExpanded && (
                <tr>
                  <td colSpan={12} style={{ padding: 0 }}>
                    {atBatsLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                        <Spinner animation="border" size="sm" variant="secondary" />
                      </div>
                    ) : (() => {
                      const yearAbs = getYearAtBats(yr);
                      if (yearAbs.length === 0) return (
                        <div style={{ padding: '8px', textAlign: 'center', color: '#6c757d', fontSize: '0.65rem' }}>No at-bat detail</div>
                      );
                      return (
                        <table style={{ width: '100%', fontSize: '0.65rem', borderCollapse: 'collapse', backgroundColor: 'rgba(20,20,30,0.5)' }}>
                          <thead>
                            <tr>
                              {['Date', 'Result', 'Count', 'Last Pitch', 'EV', 'LA', 'Dist'].map(col => (
                                <th key={col} style={{ color: '#6b7280', padding: '3px 4px', borderBottom: '1px solid #333', textAlign: col === 'Result' || col === 'Last Pitch' ? 'left' : 'center', fontSize: '0.6rem', fontWeight: 600 }}>{col}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {yearAbs.map((ab, i) => (
                              <MatchupAtBatRow
                                key={i}
                                ab={ab}
                                isExpanded={expandedAbIdx === `${yr}_${i}`}
                                onToggle={() => setExpandedAbIdx(expandedAbIdx === `${yr}_${i}` ? null : `${yr}_${i}`)}
                                onPlayClick={(playId, season) => onBallFlight && onBallFlight({ playId, season })}
                              />
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
        <MatchupStatRow s={stats} label="Total" rowStyle={{ borderTop: '2px solid #ffc107', color: '#ffc107', fontWeight: 600 }} />
      </tbody>
    </table>
    </>
  );
};

const PlayerOddsTable = ({ gameId, game, playerId, playerName, playerTeam, playerPosition, awayTeam, homeTeam, market, sport, onChartClick, onMarketChange, onLineChange, onOddsClick, onPlayerInfo, chartElement, gameLogTargetNode, opposingPitcher: opposingPitcherProp, marketBannerBottom }) => {
  const { bookOrder, isBookEnabled } = useBooks();
  const { isDarkMode } = useDarkMode();
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showOver, setShowOver] = useState(true);
  const [showUnder, setShowUnder] = useState(false);
  const [ballFlightPlay, setBallFlightPlay] = useState(null);

  // MLB Career Stats state
  const [careerData, setCareerData] = useState(null);
  const [careerLoading, setCareerLoading] = useState(false);
  const [careerSplit, setCareerSplit] = useState('all'); // 'all' | 'vsRHP' | 'vsLHP'

  // MLB Pitch Breakdown state
  const [pitchBreakdown, setPitchBreakdown] = useState(null);
  const [pitchBreakdownLoading, setPitchBreakdownLoading] = useState(false);
  const [pitchBreakdownYear, setPitchBreakdownYear] = useState(String(new Date().getFullYear()));
  const [pitchBreakdownSplits, setPitchBreakdownSplits] = useState('all'); // 'all' | 'vs_R' | 'vs_L'
  const [pitchBreakdownExpanded, setPitchBreakdownExpanded] = useState(false);
  // MLB Pitch Log (batter-only) — a filtered view of the atbats endpoint,
  // toggled per pitch type. Selection defaults to the pitch the batter has
  // seen most; user can multi-select.
  const [pitchLogExpanded, setPitchLogExpanded] = useState(false);
  const [pitchLogSelected, setPitchLogSelected] = useState([]); // e.g. ['FF']
  const [pitchLogData, setPitchLogData] = useState(null);
  const [pitchLogLoading, setPitchLogLoading] = useState(false);
  // Pitcher-only "Graphs" section — currently a per-game bar chart of outs,
  // hits, and strikeouts. Collapsed by default. `pitcherGraphSeries` holds
  // which of the three stats are currently rendered — the user can toggle
  // any subset on/off from chips above the chart.
  const [pitcherGraphsExpanded, setPitcherGraphsExpanded] = useState(false);
  const [pitcherGraphSeries, setPitcherGraphSeries] = useState({ outs: true, hits: true, k: true });
  // Pitcher-only "vs Opponent" section — hits /api/stats/mlb/{pitcherId}/{oppTeamAbbr}.
  // Collapsed by default; resets when the player or opponent changes so we don't
  // flash stale numbers when the panel switches to a different matchup.
  const [oppStatsData, setOppStatsData] = useState(null);
  const [oppStatsLoading, setOppStatsLoading] = useState(false);
  const [oppStatsExpanded, setOppStatsExpanded] = useState(false);
  // Bar-chart flow direction. `true` = recent games rendered left → older on
  // the right (default). `false` = oldest on the left, recent on the right.
  const [pitcherGraphRecentLeft, setPitcherGraphRecentLeft] = useState(true);
  // Clicked pitch on the performance radial — filters the zone heatmap to
  // just that pitch type. Null shows the season-total heatmap.
  const [selectedPitchCode, setSelectedPitchCode] = useState(null);

  // MLB Game Log state
  const [gameLogData, setGameLogData] = useState(null);
  const [gameLogLoading, setGameLogLoading] = useState(false);
  const [gameLogSeason, setGameLogSeason] = useState(2026);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [showAllGames, setShowAllGames] = useState(false);
  const [gameLogMode, setGameLogMode] = useState(null); // 'batting' | 'pitching' | null (auto)

  // At-bats detail state
  const [expandedGameIdx, setExpandedGameIdx] = useState(null);
  const [atBatsData, setAtBatsData] = useState(null);
  const [atBatsLoading, setAtBatsLoading] = useState(false);

  // Today's matchup state (batter vs opposing pitcher)
  const [matchupInfo, setMatchupInfo] = useState(null); // { pitcher, stats, hrScore }
  const [matchupLoading, setMatchupLoading] = useState(false);
  const [hrScoreExpanded, setHrScoreExpanded] = useState(false);
  // Bullpen override — mirrors the Matchups panel dropdown. When set, the
  // Today's Matchup BvP fetch swaps in this reliever in place of the game's
  // probable pitcher. Cleared when player or game changes.
  const [bullpenOverridePitcher, setBullpenOverridePitcher] = useState(null); // { id, name, hand }
  useEffect(() => { setBullpenOverridePitcher(null); }, [playerId, gameId]);

  // Today's game at-bats state
  const [todayAtBats, setTodayAtBats] = useState(null);
  const [todayAtBatsLoading, setTodayAtBatsLoading] = useState(false);

  useEffect(() => {
    if (!gameId || !playerId) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `/api/games/${sport}/${gameId}/${playerId}`;
        console.log('[PlayerOddsTable] Fetching:', url);
        const response = await fetch(url);
        if (!response.ok) {
          // 404 is the normal "player has no odds today" case (typical for
          // relievers picked from the bullpen dropdown). Don't fail the
          // whole panel — set an empty odds shape so the downstream MLB
          // sections (career, game log, matchup, pitch breakdown, graphs)
          // still render.
          if (response.status === 404) {
            setPlayerData({ availableMarkets: [] });
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        console.log('[PlayerOddsTable] Response keys:', Object.keys(data));
        console.log('[PlayerOddsTable] Response sample:', JSON.stringify(data).slice(0, 500));
        setPlayerData(data);

        // For MLB, look up hand info from game data (awayPlayers/homePlayers)
        if (sport === 'mlb' && onPlayerInfo) {
          const markets = data.availableMarkets || [];
          const isPitcher = markets.some(m => m.startsWith('pitcher_'));
          const cleanId = String(playerId).replace(/^mlb_/, '');
          const key = `ID${cleanId}`;
          const gamePlayer = game?.awayPlayers?.[key] || game?.homePlayers?.[key];
          onPlayerInfo({
            batSide: gamePlayer?.batSide?.code || null,
            pitchHand: gamePlayer?.pitchHand?.code || null,
            isPitcher,
            battingOrder: data.battingOrder || null,
            team: data.team || null
          });
        }
      } catch (err) {
        console.error('[PlayerOddsTable] Fetch error:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [gameId, playerId, sport]);

  // MLB Game Log: reset to 2026 when player changes
  useEffect(() => {
    if (sport !== 'mlb' || !playerId) return;
    setGameLogSeason(2026);
  }, [sport, playerId]);

  // Resolve the effective opposing pitcher — override takes precedence over
  // the game's probable pitcher when the bullpen dropdown is used. Memoized
  // on primitives so the fetch effect below doesn't infinite-loop from a
  // fresh object being minted on every render.
  const effectiveOpposingPitcher = useMemo(() => (
    bullpenOverridePitcher
      ? { id: bullpenOverridePitcher.id, fullName: bullpenOverridePitcher.name, throws: bullpenOverridePitcher.hand }
      : opposingPitcherProp
  ), [
    bullpenOverridePitcher?.id,
    bullpenOverridePitcher?.name,
    bullpenOverridePitcher?.hand,
    opposingPitcherProp?.id,
    opposingPitcherProp?.fullName,
    opposingPitcherProp?.throws,
  ]);

  // MLB Today's Matchup: fetch career BvP history for this batter vs opposing pitcher
  useEffect(() => {
    if (sport !== 'mlb' || !playerId || !effectiveOpposingPitcher?.id || playerPosition === 'pitcher' || playerPosition === 'P') {
      setMatchupInfo(null);
      return;
    }
    let cancelled = false;
    const fetchMatchup = async () => {
      setMatchupLoading(true);
      try {
        const cleanBatterId = String(playerId).replace(/^mlb_/, '');
        const cleanPitcherId = String(effectiveOpposingPitcher.id);
        const pitcherGameData = game?.awayPlayers?.[`ID${cleanPitcherId}`] || game?.homePlayers?.[`ID${cleanPitcherId}`];

        const pitcher = {
          id: effectiveOpposingPitcher.id,
          name: effectiveOpposingPitcher.fullName,
          pitcherHand: effectiveOpposingPitcher.throws || pitcherGameData?.pitchHand?.code || null
        };

        // Fetch career matchup directly between this batter and pitcher
        const res = await fetch(`/api/stats/mlb/atbats/matchup/${cleanBatterId}/${cleanPitcherId}`);
        if (cancelled) return;

        let stats = null;
        let seasons = null;
        let atBatsData = null;
        let hrScore = null;
        if (res.ok) {
          const data = await res.json();
          stats = data.stats || null;
          atBatsData = data.atBats || null;
          const hrScores = Array.isArray(data.hrScores) ? data.hrScores : [];
          const gamePkNum = parseInt(gameId);
          hrScore = hrScores.find(h => h.gamePk === gamePkNum) || hrScores[0] || null;
          // Build seasons from at-bats by grouping by year
          if (atBatsData && atBatsData.length > 0) {
            const byYear = {};
            atBatsData.forEach(ab => {
              const yr = ab.season || (ab.officialDate ? ab.officialDate.slice(0, 4) : null);
              if (!yr) return;
              if (!byYear[yr]) byYear[yr] = { pa: 0, ab: 0, h: 0, hr: 0, k: 0, bb: 0, doubles: 0, triples: 0, singles: 0 };
              const s = byYear[yr];
              const event = (ab.result?.event || '').toLowerCase();
              s.pa++;
              if (!/walk|hit by pitch|intentional|sac/i.test(event)) s.ab++;
              if (/single/.test(event)) { s.h++; s.singles++; }
              if (/double/.test(event) && !/double play/.test(event)) { s.h++; s.doubles++; }
              if (/triple/.test(event)) { s.h++; s.triples++; }
              if (/home run/.test(event)) { s.h++; s.hr++; }
              if (/strikeout|struck/.test(event)) s.k++;
              if (/walk|hit by pitch|intentional/.test(event)) s.bb++;
            });
            seasons = {};
            Object.entries(byYear).forEach(([yr, s]) => {
              s.avg = s.ab > 0 ? s.h / s.ab : 0;
              s.slg = s.ab > 0 ? (s.singles + s.doubles * 2 + s.triples * 3 + s.hr * 4) / s.ab : 0;
              s.ops = (s.ab > 0 ? s.h / s.ab : 0) + (s.pa > 0 ? (s.h + s.bb) / s.pa : 0);
              seasons[yr] = s;
            });
          }
        }

        setMatchupInfo({ pitcher, stats, seasons, hrScore });
      } catch {
        if (!cancelled) setMatchupInfo(null);
      } finally {
        if (!cancelled) setMatchupLoading(false);
      }
    };
    fetchMatchup();
    return () => { cancelled = true; };
  }, [sport, playerId, playerPosition, effectiveOpposingPitcher, game, gameId]);

  // MLB Career Stats: fetch career data
  useEffect(() => {
    if (sport !== 'mlb' || !playerId) {
      setCareerData(null);
      return;
    }
    let cancelled = false;
    const fetchCareer = async () => {
      setCareerLoading(true);
      try {
        const cleanId = String(playerId).replace(/^mlb_/, '');
        const res = await fetch(`/api/stats/mlb/career/${cleanId}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setCareerData(data);
        }
      } catch {
        if (!cancelled) setCareerData(null);
      } finally {
        if (!cancelled) setCareerLoading(false);
      }
    };
    fetchCareer();
    return () => { cancelled = true; };
  }, [sport, playerId]);

  // MLB Pitch Breakdown: fetch pitch-type stats for batter or pitcher
  // Bullpen-picked relievers often have no odds data (which empties out
  // `playerData.availableMarkets`) and no `playerPosition` prop. Fall back
  // to the game's roster (home/awayPlayers) position code so the pitcher
  // sections (Graphs, pitcher-specific Pitch Breakdown endpoint) still fire.
  const isPitcherPlayer = (() => {
    if (playerPosition === 'pitcher' || playerPosition === 'P') return true;
    if (playerData?.availableMarkets?.some(m => m.startsWith('pitcher_'))) return true;
    if (sport === 'mlb' && game && playerId) {
      const cleanId = String(playerId).replace(/^mlb_/, '');
      const rosterPlayer = game?.awayPlayers?.[`ID${cleanId}`] || game?.homePlayers?.[`ID${cleanId}`];
      const pos = rosterPlayer?.position;
      if (pos?.abbreviation === 'P' || pos?.code === '1' || pos?.name === 'Pitcher') return true;
    }
    return false;
  })();
  useEffect(() => {
    if (sport !== 'mlb' || !playerId) {
      setPitchBreakdown(null);
      return;
    }
    let cancelled = false;
    const fetchPitchBreakdown = async () => {
      setPitchBreakdownLoading(true);
      try {
        const cleanId = String(playerId).replace(/^mlb_/, '');
        const endpoint = isPitcherPlayer
          ? `/api/stats/mlb/atbats/pitcher/${cleanId}?season=${pitchBreakdownYear}`
          : `/api/stats/mlb/atbats/batter/${cleanId}?season=${pitchBreakdownYear}`;
        const res = await fetch(endpoint);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setPitchBreakdown(data);
        }
      } catch {
        if (!cancelled) setPitchBreakdown(null);
      } finally {
        if (!cancelled) setPitchBreakdownLoading(false);
      }
    };
    fetchPitchBreakdown();
    return () => { cancelled = true; };
  }, [sport, playerId, isPitcherPlayer, pitchBreakdownYear]);

  // Clear the wedge selection whenever the underlying data source changes.
  useEffect(() => { setSelectedPitchCode(null); }, [playerId, pitchBreakdownYear]);

  // MLB Pitch Log: default the selection to the batter's most-seen pitch
  // whenever the pitch-breakdown data (re)loads. Runs only when there's no
  // active selection so user picks aren't clobbered on re-fetch.
  useEffect(() => {
    if (isPitcherPlayer) return;
    const byPitch = pitchBreakdown?.seasonStats?.byPitch;
    if (!byPitch || pitchLogSelected.length > 0) return;
    const top = Object.entries(byPitch)
      .map(([code, d]) => ({ code, seen: d.seen || d.thrown || 0 }))
      .filter(p => p.seen > 0)
      .sort((a, b) => b.seen - a.seen)[0];
    if (top) setPitchLogSelected([top.code]);
  }, [pitchBreakdown, isPitcherPlayer, pitchLogSelected.length]);

  // Reset pitch-log selection when player changes so the auto-select above
  // fires fresh for the new batter.
  useEffect(() => { setPitchLogSelected([]); setPitchLogData(null); }, [playerId]);

  // MLB Pitch Log: fetch the filtered atbats endpoint when the selection or
  // year changes. Only fires while the section is expanded to avoid useless
  // requests when the user hasn't opened it.
  useEffect(() => {
    if (sport !== 'mlb' || isPitcherPlayer || !playerId) return;
    if (!pitchLogExpanded || pitchLogSelected.length === 0) return;
    let cancelled = false;
    const fetchPitchLog = async () => {
      setPitchLogLoading(true);
      try {
        const cleanId = String(playerId).replace(/^mlb_/, '');
        const qs = new URLSearchParams({
          seasons: pitchBreakdownYear,
          pitchTypes: pitchLogSelected.join(','),
        });
        const res = await fetch(`/api/stats/mlb/atbats/batter/${cleanId}?${qs.toString()}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setPitchLogData(data);
      } catch {
        if (!cancelled) setPitchLogData(null);
      } finally {
        if (!cancelled) setPitchLogLoading(false);
      }
    };
    fetchPitchLog();
    return () => { cancelled = true; };
  }, [sport, isPitcherPlayer, playerId, pitchBreakdownYear, pitchLogSelected, pitchLogExpanded]);

  // MLB pitcher-vs-opponent stats. Fires only for pitchers on an MLB game where
  // we know both team abbreviations. Endpoint takes the raw player id (no
  // `mlb_` prefix) and the opposing team abbreviation.
  const oppTeamAbbr = playerTeam && homeTeam && awayTeam
    ? (playerTeam === homeTeam ? awayTeam : homeTeam)
    : null;
  useEffect(() => {
    if (sport !== 'mlb' || !isPitcherPlayer || !playerId || !oppTeamAbbr) {
      setOppStatsData(null);
      return;
    }
    let cancelled = false;
    const fetchOppStats = async () => {
      setOppStatsLoading(true);
      try {
        const cleanId = String(playerId).replace(/^mlb_/, '');
        const res = await fetch(`/api/stats/mlb/${cleanId}/${oppTeamAbbr}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setOppStatsData(data);
      } catch {
        if (!cancelled) setOppStatsData(null);
      } finally {
        if (!cancelled) setOppStatsLoading(false);
      }
    };
    fetchOppStats();
    return () => { cancelled = true; };
  }, [sport, isPitcherPlayer, playerId, oppTeamAbbr]);

  // MLB Today's Game: fetch at-bats for today
  useEffect(() => {
    if (sport !== 'mlb' || !playerId) {
      setTodayAtBats(null);
      return;
    }
    let cancelled = false;
    const fetchTodayAtBats = async () => {
      setTodayAtBatsLoading(true);
      try {
        const now = new Date();
        const gameDateKey = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
        // Determine pitcher vs batter from available markets
        const markets = playerData?.availableMarkets || [];
        const isPitcher = markets.some(m => m.startsWith('pitcher_')) || playerPosition === 'pitcher' || playerPosition === 'P';
        const endpoint = isPitcher
          ? `/api/stats/mlb/atbats/${gameDateKey}/pitcher/${String(playerId).replace(/^mlb_/, '')}`
          : `/api/stats/mlb/atbats/${gameDateKey}/batter/${String(playerId).replace(/^mlb_/, '')}`;
        const res = await fetch(endpoint);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setTodayAtBats(data);
      } catch {
        if (!cancelled) setTodayAtBats(null);
      } finally {
        if (!cancelled) setTodayAtBatsLoading(false);
      }
    };
    fetchTodayAtBats();
    return () => { cancelled = true; };
  }, [sport, playerId, playerData, playerPosition]);

  // MLB Game Log: fetch data for selected season
  useEffect(() => {
    if (sport !== 'mlb' || !playerId || !gameLogSeason) return;
    let cancelled = false;

    const fetchGameLog = async () => {
      setGameLogLoading(true);
      try {
        const res = await fetch(`/api/stats/mlb/${gameLogSeason}/${playerId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setGameLogData(data.gameLog || []);
      } catch {
        if (!cancelled) setGameLogData([]);
      } finally {
        if (!cancelled) setGameLogLoading(false);
      }
    };

    fetchGameLog();
    return () => { cancelled = true; };
  }, [sport, playerId, gameLogSeason]);

  // Reset expanded at-bats and mode when player or season changes
  useEffect(() => {
    setExpandedGameIdx(null);
    setAtBatsData(null);
    setGameLogMode(null);
  }, [playerId, gameLogSeason]);

  // Fetch at-bats for a specific game
  const fetchAtBats = async (game, idx) => {
    if (expandedGameIdx === idx) {
      setExpandedGameIdx(null);
      setAtBatsData(null);
      return;
    }
    const gameIdentifier = game.gamePk || game.gameId;
    if (!gameIdentifier || !gameLogSeason) return;

    setExpandedGameIdx(idx);
    setAtBatsLoading(true);
    setAtBatsData(null);
    try {
      const res = await fetch(`/api/stats/mlb/${gameLogSeason}/${gameIdentifier}/${playerId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAtBatsData(data);
    } catch {
      setAtBatsData(null);
    } finally {
      setAtBatsLoading(false);
    }
  };

  // Skill-position NFL players (QB/RB/TE/WR/FB) get every stat tab even when
  // the book has no odds for that market, so their game-log chart stays
  // reachable. Kickers/defenders keep the normal "only markets with lines"
  // behavior.
  const isNflSkillPosition = (sport === 'nfl' || sport === 'ncaaf') &&
    !!playerPosition &&
    ['QB', 'RB', 'TE', 'WR', 'FB'].includes(String(playerPosition).toUpperCase());

  // Process data into markets structure
  const markets = React.useMemo(() => {
    if (!playerData) return [];
    // Detect MLB format: has availableMarkets AND market data uses nested { over_under, alternates } structure
    // NBA/NFL also have availableMarkets but use flat arrays with flat keys (e.g. player_points_fanduel_over)
    const hasAvailableMarkets = Array.isArray(playerData.availableMarkets);
    let isMLBFormat = false;
    if (hasAvailableMarkets && playerData.availableMarkets.length > 0) {
      const firstMarketKey = playerData.availableMarkets[0];
      const firstMarketData = playerData[firstMarketKey];
      // MLB format has { over_under: [...], alternates: [...] } objects
      // Generic format has plain arrays: [{ point, player_points_fanduel_over, ... }]
      isMLBFormat = firstMarketData && !Array.isArray(firstMarketData) && typeof firstMarketData === 'object' && (firstMarketData.over_under || firstMarketData.alternates);
    }
    let result = isMLBFormat
      ? processMLBData(playerData)
      : processGenericData(playerData, sport);

    // Field goals only make sense for kickers — hide the FG tab for every
    // other NFL/NCAAF player even if the feed somehow carries lines.
    if (sport === 'nfl' || sport === 'ncaaf') {
      const isKicker = String(playerPosition).toUpperCase() === 'PK';
      if (!isKicker) {
        result = result.filter(m => m.marketKey !== 'player_field_goals');
      }
    }

    // For skill-position NFL/NCAAF players, backfill any NFL category that
    // didn't come back with odds so every stat toggle still appears. The
    // placeholder's empty `lines` triggers the "no odds" message in the
    // table body, and the chart column still works off `marketKey`. FG is
    // kicker-only and stays out of the skill-position placeholder set.
    if (isNflSkillPosition) {
      const existing = new Set(result.map(m => m.marketKey));
      NFL_MARKET_CATEGORIES.forEach(cat => {
        if (cat.marketKey === 'player_field_goals') return;
        if (!existing.has(cat.marketKey)) {
          result.push({
            marketKey: cat.marketKey,
            marketName: cat.name,
            category: cat.category,
            lines: [],
            isPlaceholder: true,
          });
        }
      });
    }

    console.log('[PlayerOddsTable] Processed markets:', result.length, result.map(m => m.marketKey));
    if (result.length > 0) {
      console.log('[PlayerOddsTable] First market lines:', result[0].lines.length, result[0].lines[0]);
    }
    return result;
  }, [playerData, sport, isNflSkillPosition, playerPosition]);

  // Honor the user's Settings order (bookOrder) and enable state. Disabled
  // and hidden books are filtered out so the player panel matches the rest
  // of the app's columns. Falls back to BOOKMAKERS_LIST if context order
  // somehow comes back empty.
  const bookmakers = (bookOrder && bookOrder.length > 0)
    ? bookOrder.filter(book => isBookEnabled(book))
    : BOOKMAKERS_LIST;

  // Auto-select the first available market, or the one matching the incoming bet's market
  useEffect(() => {
    if (markets.length === 0) return;

    let selected = null;
    // Try to match the incoming market prop to a category
    if (market) {
      const matchByKey = markets.find(m => m.marketKey === market);
      if (matchByKey) { selected = matchByKey; }
      else {
        const matchByCat = markets.find(m => m.category === market);
        if (matchByCat) { selected = matchByCat; }
        else {
          const withPrefix = markets.find(m => m.marketKey === `player_${market}`);
          if (withPrefix) { selected = withPrefix; }
        }
      }
    }
    if (!selected && sport === 'mlb') {
      selected = markets.find(m => m.marketKey === 'batter_home_runs' || m.category === 'home_runs' || m.marketKey === 'player_home_runs');
    }
    if (!selected) selected = markets[0];
    setSelectedCategory(selected.category);
    if (onMarketChange) onMarketChange(selected.marketKey);
  }, [markets, market]);

  // Report main line value to parent whenever current market changes
  useEffect(() => {
    if (!onLineChange || markets.length === 0) return;
    const mkt = markets.find(m => m.category === selectedCategory) || markets[0];
    if (!mkt || !mkt.lines) { onLineChange(null); return; }
    let lineVal = null;
    // Find the line with the most isMain flags
    let maxMainCount = 0;
    mkt.lines.forEach(line => {
      if (line.isSpecial) return;
      let mainCount = 0;
      if (line.books) {
        Object.values(line.books).forEach(bookData => {
          if (bookData.isMain === true || bookData.over_isMain === true || bookData.under_isMain === true || bookData.yes_isMain === true) {
            mainCount++;
          }
        });
      }
      if (mainCount > maxMainCount) { maxMainCount = mainCount; lineVal = line.point; }
    });
    // Fallback: line with most books
    if (lineVal === null) {
      let maxBooks = 0;
      mkt.lines.forEach(line => {
        if (line.isSpecial) return;
        const cnt = line.books ? Object.keys(line.books).length : 0;
        if (cnt > maxBooks) { maxBooks = cnt; lineVal = line.point; }
      });
    }
    // Final fallback
    if (lineVal === null) {
      const first = mkt.lines.find(l => !l.isSpecial);
      lineVal = first?.point ?? null;
    }
    onLineChange(lineVal);
  }, [selectedCategory, markets, onLineChange]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
        <Spinner animation="border" size="sm" variant="light" />
      </div>
    );
  }

  if (error) {
    return <div style={{ color: '#ef4444', fontSize: '0.75rem', padding: '8px' }}>Error: {error}</div>;
  }

  if (!playerData) return null;

  // Odds section is optional — pitchers picked from the bullpen dropdown
  // often lack odds. When markets is empty, we still want the MLB sections
  // below (career / game log / matchup / pitch breakdown / graphs) to render.
  const hasOddsMarkets = markets.length > 0;
  const currentMarket = hasOddsMarkets ? (markets.find(m => m.category === selectedCategory) || markets[0]) : null;
  const isMLBFormat = Array.isArray(playerData.availableMarkets);
  const overUnderLines = currentMarket ? currentMarket.lines.filter(line => !line.isSpecial) : [];
  const specialLines = currentMarket ? currentMarket.lines.filter(line => line.isSpecial) : [];
  const hasOverUnder = overUnderLines.length > 0;

  return (
    <div style={{ overflow: 'auto', maxHeight: '100%', flex: 1, position: 'relative' }}>
      {!hasOddsMarkets && (
        <div style={{ padding: '8px 4px', color: '#6c757d', fontSize: '0.7rem', fontStyle: 'italic' }}>
          No odds available for this player.
        </div>
      )}
      {hasOddsMarkets && <>
      {/* Market tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', flexWrap: 'wrap' }}>
        {markets.map(m => (
          <button
            key={m.category}
            onClick={() => { setSelectedCategory(m.category); if (onMarketChange) onMarketChange(m.marketKey); }}
            style={{
              padding: '2px 8px',
              fontSize: '0.7rem',
              borderRadius: '4px',
              border: selectedCategory === m.category ? '1px solid #3B82F6' : '1px solid #4b5563',
              backgroundColor: selectedCategory === m.category ? '#3B82F6' : 'transparent',
              color: selectedCategory === m.category ? 'white' : '#9CA3AF',
              cursor: 'pointer',
              fontWeight: selectedCategory === m.category ? 600 : 400
            }}
          >
            {m.marketName}
          </button>
        ))}
      </div>

      {/* Over/Under toggles */}
      {hasOverUnder && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '6px', alignItems: 'center' }}>
          <span style={{ color: '#6b7280', fontSize: '0.65rem' }}>Show:</span>
          <button
            onClick={() => setShowOver(!showOver)}
            style={{
              padding: '1px 6px',
              fontSize: '0.65rem',
              borderRadius: '3px',
              border: showOver ? '1px solid #22c55e' : '1px solid #4b5563',
              backgroundColor: showOver ? '#22c55e' : 'transparent',
              color: showOver ? 'white' : '#9CA3AF',
              cursor: 'pointer'
            }}
          >
            Over
          </button>
          <button
            onClick={() => setShowUnder(!showUnder)}
            style={{
              padding: '1px 6px',
              fontSize: '0.65rem',
              borderRadius: '3px',
              border: showUnder ? '1px solid #22c55e' : '1px solid #4b5563',
              backgroundColor: showUnder ? '#22c55e' : 'transparent',
              color: showUnder ? 'white' : '#9CA3AF',
              cursor: 'pointer'
            }}
          >
            Under
          </button>
        </div>
      )}

      {/* Placeholder markets (skill-position NFL players with no odds for
          this stat) show a note in place of the empty bookmaker table so
          the stat chart below is still reachable. */}
      {currentMarket && currentMarket.isPlaceholder && (
        <div style={{ padding: '12px 4px', color: '#9CA3AF', fontSize: '0.75rem', fontStyle: 'italic' }}>
          No odds available for this market.
        </div>
      )}

      {/* Odds table - mimics NBAPlayerModal renderMobileTable */}
      {(!currentMarket || !currentMarket.isPlaceholder) && (
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Table bordered size="sm" style={{ fontSize: '0.7rem', marginBottom: 0, backgroundColor: 'transparent', minWidth: 'max-content' }}>
        <thead>
          <tr>
            <th style={{
              color: '#9CA3AF',
              padding: '3px 4px',
              backgroundColor: 'rgba(30,30,30,0.9)',
              border: '1px solid #333',
              textAlign: 'center',
              fontSize: '0.65rem',
              width: '60px',
              minWidth: '60px'
            }}>
              {showOver && showUnder ? 'O/U' : showOver ? 'Over' : hasOverUnder ? 'Under' : ''}
            </th>
            {bookmakers.map(book => {
              const displayBook = BOOKMAKER_ALIASES[book] || book;
              return (
                <th key={book} style={{
                  textAlign: 'center',
                  padding: '3px 2px',
                  backgroundColor: 'rgba(30,30,30,0.9)',
                  border: '1px solid #333',
                  overflow: 'hidden',
                  width: '70px',
                  minWidth: '70px'
                }}>
                  <img
                    src={`/logos/${displayBook}.png`}
                    alt={displayBook}
                    style={{ height: '14px', width: 'auto', maxWidth: '100%' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {/* Over/Under lines */}
          {overUnderLines.map((line, lineIdx) => (
            <React.Fragment key={`line-${lineIdx}`}>
              {showOver && (
                <tr>
                  {showOver && showUnder ? (
                    <td
                      rowSpan={2}
                      style={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        verticalAlign: 'middle',
                        color: '#60a5fa',
                        cursor: 'pointer',
                        backgroundColor: 'rgba(30,30,30,0.9)',
                        border: '1px solid #333',
                        padding: '3px 4px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onChartClick) onChartClick(currentMarket.marketKey, line.point, true);
                      }}
                    >
                      {line.point}
                    </td>
                  ) : (
                    <td
                      style={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        color: '#60a5fa',
                        cursor: 'pointer',
                        backgroundColor: 'rgba(30,30,30,0.9)',
                        border: '1px solid #333',
                        padding: '3px 4px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onChartClick) onChartClick(currentMarket.marketKey, line.point, true);
                      }}
                    >
                      {line.point}
                    </td>
                  )}
                  {bookmakers.map(book => {
                    let overOdds, cellStyle, isAvailable;

                    overOdds = line.books?.[book]?.over;
                    cellStyle = getTierStyle(line, book, 'over');
                    isAvailable = line.books?.[book]?.over_isAvailable !== false;

                    return (
                      <td
                        key={`${book}-over`}
                        style={{
                          textAlign: 'center',
                          padding: '3px 2px',
                          border: '1px solid #333',
                          fontSize: '0.7rem',
                          color: 'white',
                          cursor: overOdds ? 'pointer' : undefined,
                          ...cellStyle,
                          opacity: !isAvailable ? 0.25 : undefined
                        }}
                        onClick={() => overOdds && onOddsClick && onOddsClick(gameId, playerId, playerName, currentMarket.marketKey, book, overOdds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, 'over')}
                      >
                        {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : <span style={{ color: '#4b5563' }}>-</span>}
                      </td>
                    );
                  })}
                </tr>
              )}
              {showUnder && (
                <tr>
                  {!showOver && (
                    <td
                      style={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        color: '#60a5fa',
                        cursor: 'pointer',
                        backgroundColor: 'rgba(30,30,30,0.9)',
                        border: '1px solid #333',
                        padding: '3px 4px'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onChartClick) onChartClick(currentMarket.marketKey, line.point, false);
                      }}
                    >
                      {line.point}
                    </td>
                  )}
                  {bookmakers.map(book => {
                    let underOdds, cellStyle, isAvailable;

                    underOdds = line.books?.[book]?.under;
                    cellStyle = getTierStyle(line, book, 'under');
                    isAvailable = line.books?.[book]?.under_isAvailable !== false;

                    return (
                      <td
                        key={`${book}-under`}
                        style={{
                          textAlign: 'center',
                          padding: '3px 2px',
                          border: '1px solid #333',
                          fontSize: '0.7rem',
                          color: 'white',
                          cursor: underOdds ? 'pointer' : undefined,
                          ...cellStyle,
                          opacity: !isAvailable ? 0.25 : undefined
                        }}
                        onClick={() => underOdds && onOddsClick && onOddsClick(gameId, playerId, playerName, currentMarket.marketKey, book, underOdds, awayTeam, homeTeam, playerTeam, playerPosition, line.point, 'under')}
                      >
                        {underOdds ? (underOdds > 0 ? `+${underOdds}` : underOdds) : <span style={{ color: '#4b5563' }}>-</span>}
                      </td>
                    );
                  })}
                </tr>
              )}
            </React.Fragment>
          ))}

          {/* Special/binary lines (e.g. First Basket, Double Double) */}
          {specialLines.map((line, lineIdx) => (
            <tr key={`special-${lineIdx}`}>
              <td
                style={{
                  fontWeight: 'bold',
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  color: '#60a5fa',
                  cursor: 'pointer',
                  backgroundColor: 'rgba(30,30,30,0.9)',
                  border: '1px solid #333',
                  padding: '3px 4px'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onChartClick) onChartClick(line.originalMarketKey || currentMarket.marketKey, null, true);
                }}
              >
                {line.label}
              </td>
              {bookmakers.map(book => {
                const yesOdds = line.books?.[book]?.yes;
                const overOdds = line.books?.[book]?.over;
                const odds = yesOdds || overOdds;
                const side = yesOdds !== undefined && yesOdds !== null ? 'yes' : 'over';
                const cellStyle = getTierStyle(line, book, side);
                const isAvailable = line.books?.[book]?.yes_isAvailable !== false && line.books?.[book]?.over_isAvailable !== false;

                return (
                  <td
                    key={`${book}-yes`}
                    style={{
                      textAlign: 'center',
                      padding: '3px 2px',
                      border: '1px solid #333',
                      fontSize: '0.7rem',
                      color: 'white',
                      cursor: odds ? 'pointer' : undefined,
                      ...cellStyle,
                      opacity: !isAvailable ? 0.25 : undefined
                    }}
                    onClick={() => odds && onOddsClick && onOddsClick(gameId, playerId, playerName, line.originalMarketKey || currentMarket.marketKey, book, odds, awayTeam, homeTeam, playerTeam, playerPosition, null, 'yes')}
                  >
                    {odds ? (odds > 0 ? `+${odds}` : odds) : <span style={{ color: '#4b5563' }}>-</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
      </div>
      )}

      {chartElement}

      {/* NFL per-game performance chart lives in the parent (App.js) so it
          mirrors NBA/NHL/WNBA: on wide mode it occupies the right half of
          the panel; on narrow mode it stacks below. Rendering it here too
          would duplicate the chart in narrow mode. */}
      </>}

      {/* MLB Today's Game */}
      {sport === 'mlb' && todayAtBats && (
        <div style={{ marginTop: '12px' }}>
          <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Today's Game</span>
          <div style={{ marginTop: '6px', backgroundColor: 'rgba(20,20,30,0.6)', borderRadius: '8px', border: '1px solid #333', overflow: 'hidden' }}>
            <AtBatsDetail data={todayAtBats} isPitcher={isPitcherPlayer} onBallFlight={setBallFlightPlay} mobile={window.innerWidth < 768} />
          </div>
        </div>
      )}
      {sport === 'mlb' && todayAtBatsLoading && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Today's Game</span>
          <Spinner animation="border" size="sm" variant="secondary" />
        </div>
      )}

      {/* MLB Today's Matchup */}
      {sport === 'mlb' && matchupInfo?.pitcher && (() => {
        // Build the opposing bullpen list from whichever side the probable
        // pitcher is on. The batter faces relievers from that same team, so
        // whichever players map contains the probable pitcher is the one
        // whose bullpen we surface.
        // Bullpen list resolves via the shared helper — which side we use
        // depends on which team the probable pitcher belongs to. Batter faces
        // relievers from that same team.
        const pProbeId = opposingPitcherProp?.id;
        const pitcherIsHome = pProbeId != null && !!game?.homePlayers?.[`ID${pProbeId}`];
        const bullpenList = buildBullpenList(game, pitcherIsHome);
        return (
        <div style={{ marginTop: '12px' }}>
          <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Today's Matchup</span>
          <div style={{ marginTop: '6px', padding: '8px', backgroundColor: 'rgba(255,193,7,0.08)', borderRadius: '6px', border: '1px solid #333' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: matchupInfo.stats ? '8px' : 0, flexWrap: 'wrap' }}>
              <span style={{ color: '#ffc107', fontSize: '0.8rem', fontWeight: 600 }}>
                vs {matchupInfo.pitcher.name}
              </span>
              <span style={{ color: '#adb5bd', fontSize: '0.7rem' }}>
                ({matchupInfo.pitcher.pitcherHand})
              </span>
              <BullpenDropdown
                bullpenList={bullpenList}
                selectedId={bullpenOverridePitcher?.id}
                onSelect={(reliever) => setBullpenOverridePitcher(
                  reliever ? { id: reliever.id, name: reliever.name, hand: reliever.hand || '?' } : null
                )}
                mobile={window.innerWidth < 768}
              />
            </div>
            {matchupInfo.stats ? (
              <MatchupStatsTable stats={matchupInfo.stats} seasons={matchupInfo.seasons} batterId={playerId} pitcherId={matchupInfo.pitcher?.id} onBallFlight={setBallFlightPlay} />
            ) : (
              <div style={{ color: '#6c757d', fontSize: '0.7rem' }}>No career history</div>
            )}
            {matchupInfo.hrScore && (() => {
              const h = matchupInfo.hrScore;
              const c = h.components || {};
              const scoreColor = h.score >= 65 ? '#28a745' : h.score >= 55 ? '#ffc107' : '#dee2e6';
              const wxColor = h.weatherImpact > 0 ? '#28a745' : h.weatherImpact < 0 ? '#dc3545' : '#adb5bd';
              const wxText = h.weatherImpact != null ? `${h.weatherImpact > 0 ? '+' : ''}${Math.round(h.weatherImpact * 100)}%` : '--';
              const comps = [
                { label: 'BPow', value: c.batterPower, hi: 80, title: 'Batter Power' },
                { label: 'PVul', value: c.pitcherVuln, hi: 70, title: 'Pitcher Vulnerability' },
                { label: 'BVul', value: c.bullpenVuln, hi: 70, title: 'Bullpen Vulnerability' },
                { label: 'Rept', value: c.repertoireMatchup, hi: 80, title: 'Repertoire Matchup' },
                { label: 'Pltn', value: c.platoon, hi: 65, title: 'Platoon Advantage' },
                { label: 'H2H', value: c.h2hHistory, hi: 65, title: 'Head-to-Head History' },
                { label: 'Vnue', value: c.venue, hi: 60, title: 'Venue / Park Factor' },
              ];
              return (
                <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #333' }}>
                  <div
                    onClick={() => setHrScoreExpanded(v => !v)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}
                    title="Click to toggle component breakdown"
                  >
                    <span style={{ color: '#adb5bd', fontSize: '0.7rem', fontWeight: 600 }}>
                      {hrScoreExpanded ? '−' : '+'} HR Score
                    </span>
                    <span style={{ color: scoreColor, fontSize: '0.85rem', fontWeight: 700 }}>{h.score}</span>
                  </div>
                  {hrScoreExpanded && (
                    <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px 10px', fontSize: '0.65rem' }}>
                      {comps.map(({ label, value, hi, title }) => (
                        <span key={label} title={title} style={{ color: '#6c757d', cursor: 'help' }}>
                          {label} <span style={{ color: value != null && value >= hi ? '#28a745' : '#dee2e6', fontWeight: 600 }}>{value ?? '-'}</span>
                        </span>
                      ))}
                      <span title="Weather Impact on HR Probability" style={{ color: '#6c757d', cursor: 'help' }}>
                        wxHR <span style={{ color: wxColor, fontWeight: 600 }}>{wxText}</span>
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        );
      })()}
      {sport === 'mlb' && matchupLoading && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Today's Matchup</span>
          <Spinner animation="border" size="sm" variant="secondary" />
        </div>
      )}

      {/* MLB Pitch Breakdown */}
      {sport === 'mlb' && (pitchBreakdown?.seasonStats?.byPitch || pitchBreakdownLoading) && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span
              onClick={() => setPitchBreakdownExpanded(v => !v)}
              style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <span style={{ fontSize: '0.85rem', lineHeight: 1, width: '10px', display: 'inline-block', textAlign: 'center' }}>{pitchBreakdownExpanded ? '−' : '+'}</span>
              Pitch Breakdown
            </span>
            {pitchBreakdownExpanded && (
              <>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['2026', '2025', '2024'].map(yr => (
                    <button
                      key={yr}
                      onClick={() => setPitchBreakdownYear(yr)}
                      style={{
                        padding: '2px 10px', fontSize: '0.65rem', borderRadius: '4px', border: 'none', cursor: 'pointer',
                        backgroundColor: pitchBreakdownYear === yr ? '#0d6efd' : '#343a40',
                        color: 'white',
                        fontWeight: pitchBreakdownYear === yr ? 600 : 400
                      }}
                    >
                      {yr}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {pitchBreakdownLoading && <Spinner animation="border" size="sm" variant="secondary" />}
                  {['all', 'vs_R', 'vs_L'].map(s => (
                    <button
                      key={s}
                      onClick={() => setPitchBreakdownSplits(s)}
                      style={{
                        padding: '2px 10px', fontSize: '0.65rem', borderRadius: '4px', border: 'none', cursor: 'pointer',
                        backgroundColor: pitchBreakdownSplits === s ? '#6c757d' : '#343a40',
                        color: 'white',
                        fontWeight: pitchBreakdownSplits === s ? 600 : 400
                      }}
                    >
                      {s === 'all' ? 'All' : s === 'vs_R' ? (isPitcherPlayer ? 'vs RHB' : 'vs R') : (isPitcherPlayer ? 'vs LHB' : 'vs L')}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {pitchBreakdownExpanded && pitchBreakdown?.seasonStats && (
            <>
              {(pitchBreakdown.seasonStats.byZone || pitchBreakdown.seasonStats.byPitchSpeed || pitchBreakdown.seasonStats.byPitch) && (() => {
                // Zone data source resolution:
                //   1. If a split is active AND the aggregator supplies
                //      byZoneHand / byPitchZoneHand (keys "5_R", "FF_5_R"),
                //      filter to the selected batter/pitcher hand.
                //   2. If a pitch is selected on the radial, further narrow
                //      to that pitch via byPitchZone(Hand).
                //   3. Otherwise show the season-total zones.
                const splits = pitchBreakdownSplits;
                const handSuffix = splits === 'vs_R' ? 'R' : splits === 'vs_L' ? 'L' : null;
                const byPitchZoneHand = pitchBreakdown.seasonStats.byPitchZoneHand;
                const byZoneHand = pitchBreakdown.seasonStats.byZoneHand;
                let zoneData;
                if (selectedPitchCode) {
                  const source = handSuffix && byPitchZoneHand ? byPitchZoneHand : pitchBreakdown.seasonStats.byPitchZone;
                  const filtered = {};
                  if (source) {
                    if (handSuffix && byPitchZoneHand) {
                      // keys like "FF_5_R" — need to match pitch + hand
                      const prefix = `${selectedPitchCode}_`;
                      const suffix = `_${handSuffix}`;
                      Object.entries(source).forEach(([key, val]) => {
                        if (key.startsWith(prefix) && key.endsWith(suffix)) {
                          const zoneKey = key.slice(prefix.length, -suffix.length);
                          filtered[zoneKey] = val;
                        }
                      });
                    } else {
                      const prefix = `${selectedPitchCode}_`;
                      Object.entries(source).forEach(([key, val]) => {
                        if (key.startsWith(prefix)) filtered[key.slice(prefix.length)] = val;
                      });
                    }
                  }
                  zoneData = filtered;
                } else if (handSuffix && byZoneHand) {
                  const filtered = {};
                  const suffix = `_${handSuffix}`;
                  Object.entries(byZoneHand).forEach(([key, val]) => {
                    if (key.endsWith(suffix)) filtered[key.slice(0, -suffix.length)] = val;
                  });
                  zoneData = filtered;
                } else {
                  zoneData = pitchBreakdown.seasonStats.byZone;
                }
                const pitchLabelParts = [];
                if (selectedPitchCode) pitchLabelParts.push(PITCH_NAMES[selectedPitchCode] || selectedPitchCode);
                if (handSuffix) pitchLabelParts.push(isPitcherPlayer ? `vs ${handSuffix}HB` : `vs ${handSuffix}HP`);
                const pitchLabel = pitchLabelParts.length ? pitchLabelParts.join(' · ') : null;
                // Split-aware byPitch for the radial. byPitchHand keys look
                // like "FF_R"/"SL_L" — collapse back to bare pitch codes.
                let radialByPitch = pitchBreakdown.seasonStats.byPitch;
                if (handSuffix && pitchBreakdown.seasonStats.byPitchHand) {
                  radialByPitch = {};
                  const suffix = `_${handSuffix}`;
                  Object.entries(pitchBreakdown.seasonStats.byPitchHand).forEach(([key, val]) => {
                    if (key.endsWith(suffix)) {
                      radialByPitch[key.slice(0, -suffix.length)] = val;
                    }
                  });
                }
                return (
                  <div style={{ display: 'flex', gap: '16px', padding: '8px 4px', marginBottom: '4px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    {radialByPitch && (
                      <PitchPerformanceRadial
                        byPitch={radialByPitch}
                        selectedCode={selectedPitchCode}
                        onPitchClick={setSelectedPitchCode}
                        isPitcher={isPitcherPlayer}
                      />
                    )}
                    {zoneData && (
                      <ZoneHeatmap byZone={zoneData} pitchLabel={pitchLabel} isPitcher={isPitcherPlayer} />
                    )}
                    {zoneData && (
                      <ZoneHeatmap byZone={zoneData} pitchLabel={pitchLabel} isPitcher={isPitcherPlayer} metric="k" />
                    )}
                  </div>
                );
              })()}
          <div style={{ overflowX: 'auto' }}>
            {(() => {
              // Determine data source based on splits mode
              let pitchData;
              if (pitchBreakdownSplits === 'all') {
                pitchData = pitchBreakdown.seasonStats.byPitch || {};
              } else {
                // byPitchHand has keys like FF_R, SL_L — filter by pitcher hand suffix
                const hand = pitchBreakdownSplits === 'vs_R' ? 'R' : 'L';
                const byPitchHand = pitchBreakdown.seasonStats.byPitchHand || {};
                pitchData = {};
                Object.entries(byPitchHand).forEach(([key, data]) => {
                  if (key.endsWith(`_${hand}`)) {
                    const code = key.slice(0, -2);
                    pitchData[code] = data;
                  }
                });
              }

              const totalSeen = Object.values(pitchData).reduce((s, p) => s + (p.seen || p.thrown || 0), 0) || 1;
              const pitchCodeName = { FF: '4-Seam', SI: 'Sinker', FC: 'Cutter', CU: 'Curve', SL: 'Slider', CH: 'Change', ST: 'Sweeper', KC: 'Knuckle CB', FS: 'Splitter', KN: 'Knuckle' };
              const pitches = Object.entries(pitchData)
                .map(([code, data]) => {
                  const seen = data.seen || data.thrown || 0;
                  const ab = data.ab || data.bbe || 0;
                  const h = data.h || 0;
                  const hr = data.hr || 0;
                  const singles = data.singles || (h - (data.doubles || 0) - (data.triples || 0) - hr);
                  const doubles = data.doubles || data['2b'] || 0;
                  const triples = data.triples || data['3b'] || 0;
                  const avg = ab > 0 ? h / ab : 0;
                  const slg = ab > 0 ? (singles + doubles * 2 + triples * 3 + hr * 4) / ab : 0;
                  const swings = data.swings || 0;
                  const whiff = data.whiffRate ?? (swings > 0 ? (data.whiffs || 0) / swings : 0);
                  const bbe = data.bbe || 0;
                  const hhRate = data.hardHitRate ?? (bbe > 0 ? (data.hardHits || 0) / bbe : 0);
                  const brlRate = data.barrelRate ?? (bbe > 0 ? (data.barrels || 0) / bbe : 0);
                  const usage = Math.round((seen / totalSeen) * 100);
                  const k = data.k || 0;
                  return { code, name: pitchCodeName[code] || code, seen, usage, swings, avg, slg, hr, k, whiff, hhRate, brlRate, avgSpeed: data.avgSpeed };
                })
                .sort((a, b) => b.seen - a.seen);

              if (pitches.length === 0) return <div style={{ padding: '12px', textAlign: 'center', color: '#6c757d', fontSize: '0.7rem' }}>No pitch data for {pitchBreakdownYear}{pitchBreakdownSplits !== 'all' ? ` ${pitchBreakdownSplits.replace('_', ' ')}` : ''}</div>;
              const fs = '0.7rem';
              const hdr = { padding: '5px 4px', textAlign: 'center', color: '#adb5bd', fontSize: '0.65rem', fontWeight: 600 };
              const cell = { padding: '4px 4px', textAlign: 'center', fontSize: fs, color: '#dee2e6' };

              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '400px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #333' }}>
                      <th style={{ ...hdr, textAlign: 'left', paddingLeft: '8px' }}>Pitch</th>
                      <th style={hdr}>#</th>
                      <th style={hdr}>%</th>
                      <th style={hdr}>Velo</th>
                      <th style={hdr}>AVG</th>
                      <th style={hdr}>SLG</th>
                      <th style={hdr}>Sw</th>
                      <th style={hdr}>Whiff</th>
                      <th style={hdr}>HH%</th>
                      <th style={hdr}>Brl%</th>
                      <th style={hdr}>HR</th>
                      <th style={hdr}>K</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pitches.map((p, i) => (
                      <tr key={p.code} style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '32px', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                        <td style={{ ...cell, textAlign: 'left', paddingLeft: '8px', color: '#ffc107', fontWeight: 600 }}>{p.name}</td>
                        <td style={{ ...cell, color: '#adb5bd' }}>{p.seen}</td>
                        <td style={{ ...cell, color: '#adb5bd' }}>{p.usage}%</td>
                        <td style={{ ...cell, color: '#adb5bd' }}>{p.avgSpeed || '-'}</td>
                        <td style={{ ...cell, color: p.avg >= 0.300 ? '#28a745' : '#dee2e6' }}>{p.avg.toFixed(3).replace(/^0\./, '.')}</td>
                        <td style={{ ...cell, color: p.slg >= 0.500 ? '#ffc107' : '#dee2e6' }}>{p.slg.toFixed(3).replace(/^0\./, '.')}</td>
                        <td style={{ ...cell, color: '#adb5bd' }}>{p.swings}</td>
                        <td style={{ ...cell, color: p.whiff >= 0.30 ? '#dc3545' : '#dee2e6' }}>{Math.round(p.whiff * 100)}%</td>
                        <td style={{ ...cell }}>{p.hhRate >= 0.45 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(p.hhRate * 100)}%</span> : p.hhRate >= 0.40 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(p.hhRate * 100)}%</span> : `${Math.round(p.hhRate * 100)}%`}</td>
                        <td style={{ ...cell }}>{p.brlRate >= 0.15 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(p.brlRate * 100)}%</span> : p.brlRate >= 0.10 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(p.brlRate * 100)}%</span> : `${Math.round(p.brlRate * 100)}%`}</td>
                        <td style={{ ...cell, color: p.hr > 0 ? '#ffc107' : '#dee2e6' }}>{p.hr}</td>
                        <td style={{ ...cell, color: p.k > 0 ? '#adb5bd' : '#dee2e6' }}>{p.k}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
            </>
          )}
        </div>
      )}

      {/* MLB Pitch Log (batters only) — filtered atbats view, one toggle per
          pitch type seen. Defaults to the most-seen pitch. */}
      {sport === 'mlb' && !isPitcherPlayer && pitchBreakdown?.seasonStats?.byPitch && (() => {
        const byPitch = pitchBreakdown.seasonStats.byPitch;
        const pitchCodeName = { FF: '4-Seam', SI: 'Sinker', FC: 'Cutter', CU: 'Curve', SL: 'Slider', CH: 'Change', ST: 'Sweeper', KC: 'Knuckle CB', FS: 'Splitter', KN: 'Knuckle' };
        const availablePitches = Object.entries(byPitch)
          .map(([code, d]) => ({ code, seen: d.seen || d.thrown || 0 }))
          .filter(p => p.seen > 0)
          .sort((a, b) => b.seen - a.seen);
        if (availablePitches.length === 0) return null;
        const selectedSet = new Set(pitchLogSelected);
        const toggle = (code) => {
          setPitchLogSelected(prev => prev.includes(code)
            ? prev.filter(c => c !== code)
            : [...prev, code]);
        };
        const overall = pitchLogData?.seasonStats?.overall;
        const fmtRate = (r) => r == null ? '-' : `${Math.round(r * 100)}%`;
        const fmtAvg = (a) => a == null ? '-' : a.toFixed(3).replace(/^0\./, '.');
        const statCell = { padding: '4px 8px', fontSize: '0.7rem', color: '#dee2e6' };
        const statLabel = { padding: '4px 8px', fontSize: '0.6rem', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' };
        return (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span
                onClick={() => setPitchLogExpanded(v => !v)}
                style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <span style={{ fontSize: '0.85rem', lineHeight: 1, width: '10px', display: 'inline-block', textAlign: 'center' }}>{pitchLogExpanded ? '−' : '+'}</span>
                Pitch Log
              </span>
              {pitchLogExpanded && pitchLogLoading && (
                <span style={{ color: '#6c757d', fontSize: '0.65rem' }}>Loading…</span>
              )}
            </div>
            {pitchLogExpanded && (
              <>
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  {availablePitches.map(p => {
                    const active = selectedSet.has(p.code);
                    return (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() => toggle(p.code)}
                        style={{
                          border: `1px solid ${active ? '#ffc107' : '#444'}`,
                          background: active ? 'rgba(255,193,7,0.2)' : 'transparent',
                          color: active ? '#ffc107' : '#adb5bd',
                          padding: '3px 10px',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        {pitchCodeName[p.code] || p.code}
                        <span style={{ color: '#6c757d', fontWeight: 400 }}>{p.seen}</span>
                      </button>
                    );
                  })}
                </div>
                {pitchLogSelected.length === 0 ? (
                  <div style={{ padding: '10px', textAlign: 'center', color: '#6c757d', fontSize: '0.7rem' }}>
                    Toggle a pitch type to load its log.
                  </div>
                ) : overall ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))', gap: '4px', border: '1px solid #333', borderRadius: '4px', overflow: 'hidden' }}>
                    {[
                      ['PA', overall.pa], ['AB', overall.ab], ['H', overall.h], ['HR', overall.hr],
                      ['K', overall.k], ['BB', overall.bb],
                      ['AVG', fmtAvg(overall.avg)], ['SLG', fmtAvg(overall.slg)],
                      ['HH%', fmtRate(overall.hardHitRate)], ['Brl%', fmtRate(overall.barrelRate)],
                      ['Chase%', fmtRate(overall.chaseRate)], ['Whiff%', fmtRate(overall.whiffRate)],
                    ].map(([label, val]) => (
                      <div key={label} style={{ borderRight: '1px solid #333' }}>
                        <div style={statLabel}>{label}</div>
                        <div style={statCell}>{val ?? '-'}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  !pitchLogLoading && (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#6c757d', fontSize: '0.7rem' }}>
                      No log data returned for the selected pitch type(s).
                    </div>
                  )
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* MLB Graphs (pitchers only) — per-game bar chart of outs, hits, K */}
      {sport === 'mlb' && isPitcherPlayer && gameLogData && gameLogData.some(g => parseFloat(g.inningsPitched) > 0) && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span
              onClick={() => setPitcherGraphsExpanded(v => !v)}
              style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <span style={{ fontSize: '0.85rem', lineHeight: 1, width: '10px', display: 'inline-block', textAlign: 'center' }}>{pitcherGraphsExpanded ? '−' : '+'}</span>
              Graphs
            </span>
          </div>
          {pitcherGraphsExpanded && (() => {
            // Convert MLB "6.1" style innings-pitched into an outs count
            // (6 full innings + 1 extra out = 19). Falls back to 0 for
            // anything unparseable.
            const ipToOuts = (ip) => {
              if (ip == null || ip === '') return 0;
              const s = String(ip);
              const dot = s.indexOf('.');
              if (dot === -1) {
                const w = parseInt(s, 10);
                return Number.isFinite(w) ? w * 3 : 0;
              }
              const w = parseInt(s.slice(0, dot), 10);
              const f = parseInt(s.slice(dot + 1), 10);
              return (Number.isFinite(w) ? w * 3 : 0) + (Number.isFinite(f) ? f : 0);
            };
            const pitchingGames = gameLogData.filter(g => parseFloat(g.inningsPitched) > 0);
            // `pitcherGraphRecentLeft` = true (default) → newest on the left,
            // scanning right takes you into older games. Flip to sort oldest
            // → newest along the x-axis (recent on the right) when false.
            const sorted = [...pitchingGames].sort((a, b) => {
              const cmp = String(a.gameDate || '').localeCompare(String(b.gameDate || ''));
              return pitcherGraphRecentLeft ? -cmp : cmp;
            });
            const shortDate = (s) => {
              if (!s) return '';
              const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
              return m ? `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}` : String(s);
            };
            const labels = sorted.map(g => shortDate(g.gameDate));
            const outs = sorted.map(g => ipToOuts(g.inningsPitched));
            const hits = sorted.map(g => parseInt(g.hitsPitching, 10) || 0);
            const ks = sorted.map(g => parseInt(g.strikeOutsPitching, 10) || 0);
            // Palette chosen for R/G colorblind readability — blue / yellow /
            // green (no red-green pair). K stays green; Hits switches from
            // red to amber-yellow so it's distinguishable from K under
            // deuteranopia / protanopia.
            const allSeries = [
              { key: 'outs', label: 'Outs', color: '#3B82F6', fill: 'rgba(59, 130, 246, 0.85)', data: outs },
              { key: 'hits', label: 'Hits', color: '#eab308', fill: 'rgba(234, 179, 8, 0.85)',  data: hits },
              { key: 'k',    label: 'K',    color: '#22c55e', fill: 'rgba(34, 197, 94, 0.85)',  data: ks   },
            ];
            const activeSeries = allSeries.filter(s => pitcherGraphSeries[s.key]);
            const data = {
              labels,
              datasets: activeSeries.map(s => ({
                label: s.label,
                data: s.data,
                backgroundColor: s.fill,
                borderColor: s.color,
                borderWidth: 0,
              })),
            };
            // Opponent team per game — used for the logo row rendered below
            // the chart. Falls back to null when the log entry is missing a
            // team so the cell simply renders empty rather than a broken img.
            const opponents = sorted.map(g => {
              if (!g) return null;
              if (g.opponent) return g.opponent;
              const isHome = g.homeAway === 'home' || (g.team && g.team === g.homeTeam);
              return isHome ? g.awayTeam : g.homeTeam;
            });
            const options = {
              responsive: true,
              maintainAspectRatio: false,
              // Right padding + fixed y-axis width so the HTML logo row below
              // can line up its columns with the bar centers.
              layout: { padding: { right: 8 } },
              scales: {
                x: {
                  ticks: { color: '#adb5bd', font: { size: 10 } },
                  grid: { display: false },
                },
                y: {
                  beginAtZero: true,
                  ticks: { color: '#adb5bd', font: { size: 10 } },
                  grid: { color: 'rgba(255,255,255,0.05)' },
                  afterFit: (scale) => { scale.width = 32; },
                },
              },
              plugins: {
                legend: { labels: { color: '#dee2e6', font: { size: 11 }, boxWidth: 12 } },
                tooltip: {
                  callbacks: {
                    title: (items) => {
                      const idx = items[0]?.dataIndex ?? 0;
                      const g = sorted[idx];
                      const parts = [];
                      if (g?.gameDate) parts.push(shortDate(g.gameDate));
                      if (g?.opponent) parts.push(`${g.homeAway === 'home' ? 'vs' : '@'} ${g.opponent}`);
                      return parts.join(' · ');
                    },
                    label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}`,
                  },
                },
              },
            };
            return (
              <>
                {/* Per-series on/off chips + flow-direction toggle. Click a
                    chip to toggle a stat in or out of the chart. At least one
                    series should stay on — if the user turns all three off the
                    chart shows an empty state. The direction pill flips the
                    x-axis so recent games can be shown on the left (default)
                    or the right. */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {allSeries.map(s => {
                    const active = !!pitcherGraphSeries[s.key];
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setPitcherGraphSeries(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                        style={{
                          border: `1px solid ${active ? s.color : '#444'}`,
                          background: active ? s.fill : 'transparent',
                          color: active ? '#fff' : '#adb5bd',
                          padding: '2px 10px',
                          borderRadius: '4px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}
                      >
                        <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: active ? '#fff' : s.color, display: 'inline-block' }} />
                        {s.label}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setPitcherGraphRecentLeft(v => !v)}
                    title={pitcherGraphRecentLeft ? 'Newest on the left (click to flip)' : 'Newest on the right (click to flip)'}
                    style={{
                      marginLeft: 'auto',
                      border: '1px solid #444',
                      background: 'transparent',
                      color: '#adb5bd',
                      padding: '2px 10px',
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <span style={{ color: pitcherGraphRecentLeft ? '#ffc107' : '#6c757d' }}>←</span>
                    {pitcherGraphRecentLeft ? 'Newest · Oldest' : 'Oldest · Newest'}
                    <span style={{ color: pitcherGraphRecentLeft ? '#6c757d' : '#ffc107' }}>→</span>
                  </button>
                </div>
                <div style={{ height: sorted.length > 25 ? '280px' : '220px' }}>
                  {activeSeries.length === 0 ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6c757d', fontSize: '0.7rem' }}>
                      Toggle a stat on to view the chart.
                    </div>
                  ) : (
                    <Bar data={data} options={options} />
                  )}
                </div>
                {activeSeries.length > 0 && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${sorted.length}, 1fr)`,
                      paddingLeft: '32px',   // matches scale.y.afterFit width
                      paddingRight: '8px',   // matches layout.padding.right
                      marginTop: '2px',
                    }}
                  >
                    {opponents.map((opp, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {opp ? (
                          <img
                            src={getTeamLogoUrl(opp, isDarkMode, 'mlb')}
                            alt={opp}
                            title={opp}
                            style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* MLB pitcher vs Opponent — fetches /api/stats/mlb/{pitcherId}/{oppTeamAbbr}.
          Shows a totals strip (career-vs-team headline) plus a per-game log. */}
      {sport === 'mlb' && isPitcherPlayer && oppTeamAbbr && (oppStatsData || oppStatsLoading) && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span
              onClick={() => setOppStatsExpanded(v => !v)}
              style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <span style={{ fontSize: '0.85rem', lineHeight: 1, width: '10px', display: 'inline-block', textAlign: 'center' }}>{oppStatsExpanded ? '−' : '+'}</span>
              vs {oppTeamAbbr}
              {oppStatsData?.totals && (
                <span style={{ color: '#6c757d', fontWeight: 500, fontSize: '0.7rem', marginLeft: '4px' }}>
                  ({oppStatsData.totals.games} G, {oppStatsData.totals.inningsPitched} IP, {oppStatsData.totals.era?.toFixed(2)} ERA)
                </span>
              )}
            </span>
          </div>
          {oppStatsExpanded && (
            oppStatsLoading ? (
              <div style={{ color: '#9CA3AF', fontSize: '0.75rem', padding: '6px 2px' }}>Loading…</div>
            ) : (() => {
              const totals = oppStatsData?.totals;
              const games = Array.isArray(oppStatsData?.games) ? oppStatsData.games : [];
              if (!totals && games.length === 0) {
                return <div style={{ color: '#9CA3AF', fontSize: '0.75rem', padding: '6px 2px' }}>No data</div>;
              }

              const hdr = { padding: '5px 4px', textAlign: 'center', color: '#adb5bd', fontSize: '0.65rem', fontWeight: 600 };
              const cell = { padding: '4px 4px', textAlign: 'center', fontSize: '0.7rem', color: '#dee2e6' };
              const decisionColor = (d) => d === 'W' ? '#28a745' : d === 'L' ? '#dc3545' : '#adb5bd';
              const sortedGames = [...games].sort((a, b) => String(b.gameDate || '').localeCompare(String(a.gameDate || '')));

              return (
                <>
                  {totals && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: '6px', marginBottom: '8px' }}>
                      {[
                        ['G', totals.games],
                        ['GS', totals.starts],
                        ['W-L', `${totals.wins ?? 0}-${totals.losses ?? 0}`],
                        ['IP', totals.inningsPitched],
                        ['ERA', totals.era?.toFixed(2)],
                        ['WHIP', totals.whip?.toFixed(2)],
                        ['K/9', totals.k9?.toFixed(2)],
                        ['BB/9', totals.bb9?.toFixed(2)],
                        ['HR/9', totals.hr9?.toFixed(2)],
                        ['K', totals.strikeouts],
                        ['BB', totals.walks],
                        ['H', totals.hits],
                        ['HR', totals.homeRuns],
                        ['Str%', totals.strikePct != null ? `${totals.strikePct.toFixed(1)}%` : null],
                      ].filter(([, v]) => v != null && v !== '').map(([k, v]) => (
                        <div key={k} style={{ background: '#1e2228', border: '1px solid #333', borderRadius: '4px', padding: '4px 6px', textAlign: 'center' }}>
                          <div style={{ color: '#9CA3AF', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.03em' }}>{k}</div>
                          <div style={{ color: '#e5e7eb', fontWeight: 600, fontSize: '0.8rem' }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {sortedGames.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '540px' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #333' }}>
                            <th style={{ ...hdr, textAlign: 'left', paddingLeft: '8px' }}>Date</th>
                            <th style={hdr}>@/vs</th>
                            <th style={hdr}>Score</th>
                            <th style={hdr}>Dec</th>
                            <th style={hdr}>IP</th>
                            <th style={hdr}>H</th>
                            <th style={hdr}>ER</th>
                            <th style={hdr}>BB</th>
                            <th style={hdr}>K</th>
                            <th style={hdr}>HR</th>
                            <th style={hdr}>P-S</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedGames.map((g, i) => (
                            <tr key={g.gamePk || g.gameId || i} style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '30px', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                              <td style={{ ...cell, textAlign: 'left', paddingLeft: '8px', color: '#ffc107', fontWeight: 600 }}>{g.gameDate}</td>
                              <td style={{ ...cell, color: '#adb5bd' }}>{g.home ? 'vs' : '@'} {g.opponent}</td>
                              <td style={cell}>{g.score ? `${g.score.for}-${g.score.against}` : '-'}</td>
                              <td style={{ ...cell, color: decisionColor(g.decision), fontWeight: g.decision ? 600 : 400 }}>{g.decision || '-'}</td>
                              <td style={cell}>{g.inningsPitched}</td>
                              <td style={cell}>{g.hits}</td>
                              <td style={{ ...cell, color: (g.earnedRuns || 0) >= 4 ? '#dc3545' : '#dee2e6' }}>{g.earnedRuns}</td>
                              <td style={cell}>{g.walks}</td>
                              <td style={{ ...cell, color: (g.strikeouts || 0) >= 8 ? '#28a745' : '#dee2e6' }}>{g.strikeouts}</td>
                              <td style={{ ...cell, color: (g.homeRuns || 0) > 0 ? '#ffc107' : '#dee2e6' }}>{g.homeRuns}</td>
                              <td style={{ ...cell, color: '#adb5bd' }}>{g.pitches != null && g.strikes != null ? `${g.pitches}-${g.strikes}` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()
          )}
        </div>
      )}

      {/* MLB Career Stats */}
      {sport === 'mlb' && careerData && (
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Career Stats</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['all', 'vsRHP', 'vsLHP'].map(s => (
                <button key={s} onClick={() => setCareerSplit(s)} style={{
                  backgroundColor: careerSplit === s ? '#6c757d' : '#2a2d31',
                  color: careerSplit === s ? '#fff' : '#6c757d',
                  border: 'none', borderRadius: '4px', padding: '2px 8px',
                  fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer'
                }}>
                  {s === 'all' ? 'All' : s === 'vsRHP' ? 'vs R' : 'vs L'}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginTop: '6px', overflowX: 'auto' }}>
            {(() => {
              const seasons = isPitcherPlayer ? (careerData.pitching || []) : (careerData.batting || []);
              if (seasons.length === 0) return <div style={{ color: '#6c757d', fontSize: '0.7rem', textAlign: 'center', padding: '10px' }}>No career data</div>;

              const sorted = [...seasons].sort((a, b) => b.season.localeCompare(a.season));
              // Pitchers' splits are keyed against the batter's hand (vsRHB/vsLHB); batters' against the pitcher's (vsRHP/vsLHP)
              const splitKey = careerSplit === 'all'
                ? 'overall'
                : isPitcherPlayer
                  ? (careerSplit === 'vsRHP' ? 'vsRHB' : 'vsLHB')
                  : careerSplit;
              const fs = '0.7rem';
              const hdr = { padding: '5px 4px', textAlign: 'center', color: '#adb5bd', fontSize: '0.65rem', fontWeight: 600 };
              const cell = { padding: '4px 4px', textAlign: 'center', fontSize: fs, color: '#dee2e6' };

              if (isPitcherPlayer) {
                return (
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #333' }}>
                        <th style={{ ...hdr, textAlign: 'left', paddingLeft: '8px' }}>Year</th>
                        <th style={hdr}>Team</th>
                        <th style={hdr}>G</th>
                        <th style={hdr}>GS</th>
                        <th style={hdr}>W</th>
                        <th style={hdr}>L</th>
                        <th style={hdr}>IP</th>
                        <th style={hdr}>H/9</th>
                        <th style={hdr}>K</th>
                        <th style={hdr}>BB</th>
                        <th style={hdr}>HR/9</th>
                        <th style={hdr}>AVG</th>
                        <th style={hdr}>SLG</th>
                        <th style={hdr}>BABIP</th>
                        <th style={hdr}>HH%</th>
                        <th style={hdr}>Brl%</th>
                        <th style={hdr}>Chase</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((s, i) => {
                        const o = s.seasonStats?.[splitKey] || {};
                        if (careerSplit !== 'all' && Object.keys(o).length === 0) return null;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '32px', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                            <td style={{ ...cell, textAlign: 'left', paddingLeft: '8px', color: '#ffc107', fontWeight: 600 }}>{s.season}</td>
                            <td style={{ ...cell, color: '#adb5bd' }}>{s.team}</td>
                            <td style={cell}>{s.gamesPlayed ?? o.g ?? '-'}</td>
                            <td style={cell}>{o.gs ?? o.gamesStarted ?? '-'}</td>
                            <td style={{ ...cell, color: (o.w || 0) > 0 ? '#28a745' : '#dee2e6' }}>{o.w ?? o.wins ?? '-'}</td>
                            <td style={{ ...cell, color: (o.l || 0) > 0 ? '#dc3545' : '#dee2e6' }}>{o.l ?? o.losses ?? '-'}</td>
                            <td style={cell}>{o.ip != null ? (() => { const ip = Number(o.ip); const full = Math.floor(ip); const dec = ip - full; if (dec < 0.1) return `${full}.0`; if (dec < 0.5) return `${full}.1`; return `${full}.2`; })() : '-'}</td>
                            <td style={cell}>{(() => { const h = o.h ?? o.hits; const ip = Number(o.ip); return h != null && ip > 0 ? ((h * 9) / ip).toFixed(1) : '-'; })()}</td>
                            <td style={cell}>{o.k ?? '-'}</td>
                            <td style={cell}>{o.bb ?? '-'}</td>
                            <td style={{ ...cell, color: (() => { const hr = o.hr; const ip = Number(o.ip); return hr != null && ip > 0 && (hr * 9) / ip >= 1.5 ? '#ffc107' : '#dee2e6'; })() }}>{(() => { const hr = o.hr; const ip = Number(o.ip); return hr != null && ip > 0 ? ((hr * 9) / ip).toFixed(2) : '-'; })()}</td>
                            <td style={{ ...cell, color: (o.avg || 0) >= 0.280 ? '#dc3545' : '#dee2e6' }}>{o.avg != null ? o.avg.toFixed(3).replace(/^0\./, '.') : '-'}</td>
                            <td style={{ ...cell, color: (o.slg || 0) >= 0.450 ? '#dc3545' : '#dee2e6' }}>{o.slg != null ? o.slg.toFixed(3).replace(/^0\./, '.') : '-'}</td>
                            <td style={{ ...cell, color: (o.babip || 0) >= 0.320 ? '#dc3545' : '#dee2e6' }}>{o.babip != null ? o.babip.toFixed(3).replace(/^0\./, '.') : '-'}</td>
                            <td style={{ ...cell, color: (o.hardHitRate || 0) >= 0.40 ? '#dc3545' : '#dee2e6' }}>{o.hardHitRate != null ? `${Math.round(o.hardHitRate * 100)}%` : '-'}</td>
                            <td style={{ ...cell, color: (o.barrelRate || 0) >= 0.10 ? '#dc3545' : '#dee2e6' }}>{o.barrelRate != null ? `${Math.round(o.barrelRate * 100)}%` : '-'}</td>
                            <td style={cell}>{o.chaseRate != null ? `${Math.round(o.chaseRate * 100)}%` : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              }

              // Batter career stats
              return (
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #333' }}>
                      <th style={{ ...hdr, textAlign: 'left', paddingLeft: '8px' }}>Year</th>
                      <th style={hdr}>Team</th>
                      <th style={hdr}>PA</th>
                      <th style={hdr}>AB</th>
                      <th style={hdr}>AVG</th>
                      <th style={hdr}>SLG</th>
                      <th style={hdr}>OPS</th>
                      <th style={hdr}>2B</th>
                      <th style={hdr}>3B</th>
                      <th style={hdr}>HR</th>
                      <th style={hdr}>R</th>
                      <th style={hdr}>RBI</th>
                      <th style={hdr}>HH%</th>
                      <th style={hdr}>Brl%</th>
                      <th style={hdr}>K%</th>
                      <th style={hdr}>Chase</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((s, i) => {
                      const o = s.seasonStats?.[splitKey] || {};
                      if (careerSplit !== 'all' && Object.keys(o).length === 0) return null;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(51,51,51,0.5)', height: '32px', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                          <td style={{ ...cell, textAlign: 'left', paddingLeft: '8px', color: '#ffc107', fontWeight: 600 }}>{s.season}</td>
                          <td style={{ ...cell, color: '#adb5bd' }}>{s.team}</td>
                          <td style={cell}>{o.pa ?? '-'}</td>
                          <td style={cell}>{o.ab ?? '-'}</td>
                          <td style={{ ...cell, color: (o.avg || 0) >= 0.300 ? '#28a745' : '#dee2e6' }}>{o.avg != null ? o.avg.toFixed(3).replace(/^0\./, '.') : '-'}</td>
                          <td style={{ ...cell, color: (o.slg || 0) >= 0.500 ? '#ffc107' : '#dee2e6' }}>{o.slg != null ? o.slg.toFixed(3).replace(/^0\./, '.') : '-'}</td>
                          <td style={{ ...cell, color: (o.ops || 0) >= 0.800 ? '#28a745' : '#dee2e6', fontWeight: 600 }}>{o.ops != null ? o.ops.toFixed(3).replace(/^0\./, '.') : '-'}</td>
                          <td style={cell}>{o.doubles ?? o['2b'] ?? '-'}</td>
                          <td style={cell}>{o.triples ?? o['3b'] ?? '-'}</td>
                          <td style={{ ...cell, color: (o.hr || 0) > 0 ? '#ffc107' : '#dee2e6' }}>{o.hr ?? '-'}</td>
                          <td style={cell}>{o.r ?? o.runs ?? '-'}</td>
                          <td style={cell}>{o.rbi ?? '-'}</td>
                          <td style={{ ...cell }}>{(o.hardHitRate || 0) >= 0.45 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(o.hardHitRate * 100)}%</span> : (o.hardHitRate || 0) >= 0.40 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(o.hardHitRate * 100)}%</span> : o.hardHitRate != null ? `${Math.round(o.hardHitRate * 100)}%` : '-'}</td>
                          <td style={{ ...cell }}>{(o.barrelRate || 0) >= 0.15 ? <span style={{ backgroundColor: 'green', color: 'yellow', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(o.barrelRate * 100)}%</span> : (o.barrelRate || 0) >= 0.10 ? <span style={{ backgroundColor: '#F59E0B', color: '#1E40AF', padding: '1px 4px', borderRadius: '3px', fontWeight: 'bold' }}>{Math.round(o.barrelRate * 100)}%</span> : o.barrelRate != null ? `${Math.round(o.barrelRate * 100)}%` : '-'}</td>
                          <td style={{ ...cell, color: (o.kRate || 0) >= 0.25 ? '#dc3545' : '#dee2e6' }}>{o.kRate != null ? `${Math.round(o.kRate * 100)}%` : '-'}</td>
                          <td style={{ ...cell, color: (o.chaseRate || 0) >= 0.35 ? '#dc3545' : '#dee2e6' }}>{o.chaseRate != null ? `${Math.round(o.chaseRate * 100)}%` : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      )}
      {sport === 'mlb' && careerLoading && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Career Stats</span>
          <Spinner animation="border" size="sm" variant="secondary" />
        </div>
      )}

      {/* MLB Game Log */}
      {sport === 'mlb' && (() => {
        const gameLogContent = (
        <div style={{ marginTop: gameLogTargetNode ? 0 : '12px' }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ color: '#9CA3AF', fontSize: '0.75rem', fontWeight: 600 }}>Game Log</span>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[2026, 2025, 2024, 2023].map(season => (
                <button
                  key={season}
                  onClick={() => { setGameLogSeason(season); setShowAllGames(false); }}
                  style={{
                    padding: '2px 10px',
                    fontSize: '0.65rem',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: gameLogSeason === season ? '#0d6efd' : '#343a40',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: gameLogSeason === season ? 600 : 400
                  }}
                >
                  {season}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowAllGames(!showAllGames)}
              style={{
                padding: '1px 6px',
                fontSize: '0.6rem',
                borderRadius: '3px',
                border: '1px solid #4b5563',
                backgroundColor: 'transparent',
                color: '#9CA3AF',
                cursor: 'pointer'
              }}
            >
              {showAllGames ? 'Last 10' : 'Full'}
            </button>
          </div>

          {gameLogLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
              <Spinner animation="border" size="sm" variant="secondary" />
            </div>
          ) : gameLogData && gameLogData.length > 0 ? (() => {
            const hasPitching = gameLogData.some(g => parseFloat(g.inningsPitched) > 0);
            const hasBatting = gameLogData.some(g => parseInt(g.atBats) > 0 || parseInt(g.hits) > 0 || parseInt(g.runs) > 0);
            const isTwoWay = hasPitching && hasBatting;
            const isPitcher = isTwoWay ? (gameLogMode || 'batting') === 'pitching' : hasPitching;
            const displayGames = showAllGames ? gameLogData : gameLogData.slice(0, 10);

            return (
              <>
              {isTwoWay && (
                <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                  {['batting', 'pitching'].map(mode => (
                    <button
                      key={mode}
                      onClick={() => { setGameLogMode(mode); setExpandedGameIdx(null); setAtBatsData(null); }}
                      style={{
                        padding: '2px 10px',
                        fontSize: '0.65rem',
                        borderRadius: '4px',
                        border: 'none',
                        backgroundColor: (gameLogMode || 'batting') === mode ? '#0d6efd' : '#343a40',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: (gameLogMode || 'batting') === mode ? 600 : 400
                      }}
                    >
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
              )}
              <Table bordered size="sm" style={{ fontSize: '0.7rem', marginBottom: 0, backgroundColor: 'transparent', tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  {isPitcher ? (
                    <>
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '9%' }} />
                      <col style={{ width: '10%' }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '5%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                    </>
                  )}
                </colgroup>
                <thead>
                  <tr>
                    {isPitcher ? (
                      <>
                        <th style={glHeaderStyle}>Date</th>
                        <th style={glHeaderStyle}>vs</th>
                        <th style={glHeaderStyle}>Score</th>
                        <th style={glHeaderStyle}>IP</th>
                        <th style={glHeaderStyle}>PC</th>
                        <th style={glHeaderStyle}>K</th>
                        <th style={glHeaderStyle}>H</th>
                        <th style={glHeaderStyle}>BB</th>
                        <th style={glHeaderStyle}>ER</th>
                        <th style={glHeaderStyle}>HR</th>
                        <th style={glHeaderStyle}>W/L/S</th>
                        <th style={glHeaderStyle}>ERA</th>
                      </>
                    ) : (
                      <>
                        <th style={glHeaderStyle}>Date</th>
                        <th style={glHeaderStyle}>vs</th>
                        <th style={glHeaderStyle}>Score</th>
                        <th style={glHeaderStyle}>BAT</th>
                        <th style={glHeaderStyle}>AB</th>
                        <th style={glHeaderStyle}>H</th>
                        <th style={glHeaderStyle}>2B</th>
                        <th style={glHeaderStyle}>3B</th>
                        <th style={glHeaderStyle}>HR</th>
                        <th style={glHeaderStyle}>RBI</th>
                        <th style={glHeaderStyle}>R</th>
                        <th style={glHeaderStyle}>BB</th>
                        <th style={glHeaderStyle}>K</th>
                        <th style={glHeaderStyle}>SB</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {displayGames.map((game, idx) => {
                    const dateParts = (game.gameDate || '').split('-');
                    const dateStr = dateParts.length === 3 ? `${parseInt(dateParts[1])}/${parseInt(dateParts[2])}` : game.gameDate;
                    const isHome = game.team === game.homeTeam;
                    const opponent = isHome ? `vs ${game.awayTeam}` : `@ ${game.homeTeam}`;
                    const playerTeamScore = isHome ? game.homeScore : game.awayScore;
                    const opponentScore = isHome ? game.awayScore : game.homeScore;
                    const won = playerTeamScore > opponentScore;
                    const scoreStr = `${game.awayScore}-${game.homeScore}`;
                    const scoreColor = won ? '#22c55e' : '#ef4444';
                    const isExpanded = expandedGameIdx === idx;
                    const hasGameId = !!(game.gamePk || game.gameId);
                    const colCount = isPitcher ? 12 : 14;

                    const wls = isPitcher
                      ? (game.win ? 'W' : game.loss ? 'L' : game.save ? 'SV' : '-')
                      : null;

                    return (
                      <React.Fragment key={idx}>
                        <tr>
                          <td
                            style={{
                              ...glCellStyle,
                              cursor: hasGameId ? 'pointer' : undefined,
                              color: isExpanded ? '#60a5fa' : hasGameId ? '#93c5fd' : 'white',
                              textDecoration: hasGameId ? 'underline' : undefined,
                              textDecorationColor: hasGameId ? '#4b5563' : undefined
                            }}
                            onClick={() => hasGameId && fetchAtBats(game, idx)}
                          >
                            {dateStr}
                          </td>
                          <td style={glCellStyle}>{opponent}</td>
                          <td style={{ ...glCellStyle, color: scoreColor }}>{scoreStr}</td>
                          {isPitcher ? (
                            <>
                              <td style={glCellStyle}>{game.inningsPitched}</td>
                              <td style={glCellStyle}>{game.pitchesThrown ?? '-'}</td>
                              <td style={glCellStyle}>{game.strikeOutsPitching}</td>
                              <td style={glCellStyle}>{game.hitsPitching}</td>
                              <td style={glCellStyle}>{game.walksAllowed}</td>
                              <td style={glCellStyle}>{game.earnedRuns}</td>
                              <td style={glCellStyle}>{game.homeRunsAllowed}</td>
                              <td style={{ ...glCellStyle, fontWeight: wls !== '-' ? 'bold' : 'normal', color: wls === 'W' ? '#22c55e' : wls === 'L' ? '#ef4444' : wls === 'SV' ? '#60a5fa' : undefined }}>{wls}</td>
                              <td style={glCellStyle}>{game.era != null ? parseFloat(game.era).toFixed(2) : '-'}</td>
                            </>
                          ) : (
                            <>
                              <td style={glCellStyle}>
                                {game.battingOrderPosition == null ? '-' : (() => {
                                  // Leading `*` when the raw battingOrder isn't a whole
                                  // slot (e.g. 501 → substitute, not the original starter).
                                  // Trailing `*` when the starter was pinch-hit for.
                                  const rawOrder = game.battingOrder != null ? String(game.battingOrder) : '';
                                  const isSub = rawOrder && !rawOrder.endsWith('00');
                                  return `${isSub ? '*' : ''}${game.battingOrderPosition}${game.wasPinchHitFor ? '*' : ''}`;
                                })()}
                              </td>
                              <td style={glCellStyle}>{game.atBats}</td>
                              <td style={glCellStyle}>{game.hits}</td>
                              <td style={glCellStyle}>{game.doubles ?? 0}</td>
                              <td style={glCellStyle}>{game.triples ?? 0}</td>
                              <td style={glCellStyle}>{game.homeRuns}</td>
                              <td style={glCellStyle}>{game.rbi}</td>
                              <td style={glCellStyle}>{game.runs}</td>
                              <td style={glCellStyle}>{game.baseOnBalls}</td>
                              <td style={glCellStyle}>{game.strikeOuts}</td>
                              <td style={glCellStyle}>{game.stolenBases}</td>
                            </>
                          )}
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={colCount} style={{ padding: 0, border: '1px solid #333', backgroundColor: 'rgba(20,20,30,0.95)' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #333', backgroundColor: '#1a1d21' }}>
                                <span style={{ color: '#9CA3AF', fontSize: '0.7rem', fontWeight: 600 }}>At-Bats</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setExpandedGameIdx(null); setAtBatsData(null); }}
                                  style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                                >×</button>
                              </div>
                              {atBatsLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
                                  <Spinner animation="border" size="sm" variant="secondary" />
                                </div>
                              ) : atBatsData ? (
                                <AtBatsDetail data={atBatsData} isPitcher={isPitcher} onBallFlight={setBallFlightPlay} mobile={window.innerWidth < 768} />
                              ) : (
                                <div style={{ color: '#6b7280', fontSize: '0.65rem', textAlign: 'center', padding: '8px' }}>
                                  No at-bat data available
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
              </>
            );
          })() : gameLogData && gameLogData.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: '0.7rem', textAlign: 'center', padding: '10px' }}>
              No game log data for {gameLogSeason}
            </div>
          ) : null}
        </div>
        );
        return gameLogTargetNode
          ? ReactDOM.createPortal(gameLogContent, gameLogTargetNode)
          : gameLogContent;
      })()}
      {ballFlightPlay && <BallFlightViewer playId={ballFlightPlay.playId} season={ballFlightPlay.season} onClose={() => setBallFlightPlay(null)} topOffset={marketBannerBottom} />}
    </div>
  );
};

// Game log shared styles
const glHeaderStyle = {
  color: '#9CA3AF',
  padding: '3px 4px',
  backgroundColor: 'rgba(30,30,30,0.9)',
  border: '1px solid #333',
  textAlign: 'center',
  fontSize: '0.65rem',
  fontWeight: 600
};

const glCellStyle = {
  textAlign: 'center',
  padding: '3px 4px',
  border: '1px solid #333',
  fontSize: '0.7rem',
  color: 'white'
};

export { PitchZoneGrid };
export default PlayerOddsTable;
